import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import heic2any from "heic2any";
import {
  Calendar,
  CalendarRange,
  CalendarDays,
  MapPin,
  Maximize,
  Minimize,
  Camera,
  FolderOpen,
  Moon,
  MoonStar,
  Sun,
  ArrowLeft,
  Clock,
  X,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ZoomIn,
  ZoomOut,
  Search,
  LayoutGrid,
  Settings,
  Sunrise as SunriseIcon,
  Sunset,
  CloudSun,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import ImageUploadDialog from "../components/TeleView/ImageUploadDialog";
import CountdownOverlay from "../components/TeleView/CountdownOverlay";
import TVSettingsDialog from "../components/TeleView/TVSettingsDialog";
import TVControls from "../components/TeleView/TVControls";
import { useCountdownOverlay } from "../hooks/useCountdownOverlay";
import { useTVSettings, VIEW_MODES, THEME_MODES } from "../hooks/useTVSettings";
import ayatData from "../data/ayat.json";
import QRCode from "react-qr-code";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme as useAppTheme } from "../theme/ThemeContext";
import { auth } from "../config/firebase";
import { checkAdmin, getTeleviewImages, getTeleviewImagesMetadata, getTvData, getEvents } from "../services/api";
import WakeLockVideo from "../components/WakeLockVideo";
import {
  syncWithServer,
  saveImage,
  deleteImage as deleteCachedImage,
  createObjectUrlsFromCache,
  getCachedImagesForMasjid,
} from "../services/televiewImageCache";
import {
  acquireTvSse,
  releaseTvSse,
  addTvCommandListener,
  addTvStatusListener,
  updateTvLastView,
  updateTvSettings,
  updateTvOverlayState,
} from "../services/tvSseManager";

// SSE API URL for TV remote control
const MASJID_API = import.meta.env.VITE_MASJID_API;

// Set to true to show Adhan/Iqama test buttons in the controls
const TEST_MODE = false;
const REMOTE_SETTINGS_COMMANDS = new Set([
  "changeTheme",
  "resetSettings",
  "setThemeMode",
  "setPrimaryColor",
  "setCustomColor",
  "setShowTomorrow",
  "setZoomLevel",
  "setAdhanEnabled",
  "setIqamaEnabled",
  "setAdhanCountdownMinutes",
  "setIqamaCountdownMinutes",
  "setAdhanScreenDurationMinutes",
  "setIqamaScreenDurationMinutes",
  "setJummahSilenceEnabled",
  "setJummahSilenceDurationMinutes",
  "setForbiddenScreenEnabled",
  "setForbiddenScreenDurationMinutes",
  "updateSettings",
]);
const REMOTE_SETTINGS_SUPPRESS_MS = 1200;
const REMOTE_VIEW_SUPPRESS_MS = 1200;

/**
 * Convert HEIC/HEIF images to JPEG for browser compatibility.
 * Browsers don't natively support HEIC, so we convert on the client side.
 * @param {Blob} blob - The image blob to potentially convert
 * @param {string} filename - The filename to check for HEIC extension
 * @returns {Promise<Blob>} - The converted JPEG blob or original blob
 */
const convertHeicIfNeeded = async (blob, filename = "") => {
  // Check by MIME type
  const isHeicMime = 
    blob.type === "image/heic" || 
    blob.type === "image/heif" ||
    blob.type === "application/octet-stream"; // Server might not recognize HEIC type
  
  // Check by file extension (more reliable)
  const isHeicExtension = 
    /\.heic$/i.test(filename) ||
    /\.heif$/i.test(filename);
  
  // If extension says HEIC, trust that over MIME type
  if (!isHeicExtension && !isHeicMime) return blob;
  
  // Only convert if extension indicates HEIC (MIME type alone for octet-stream isn't reliable)
  if (!isHeicExtension) return blob;
  
  try {
    console.log(`Converting HEIC image: ${filename} (type: ${blob.type})`);
    const convertedBlob = await heic2any({
      blob,
      toType: "image/jpeg",
      quality: 0.9,
    });
    // heic2any may return an array for multi-image HEIC files
    const result = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
    console.log(`HEIC conversion complete: ${filename}, new type: ${result.type}`);
    return result;
  } catch (error) {
    console.error(`Failed to convert HEIC image ${filename}:`, error);
    // Return original blob if conversion fails
    return blob;
  }
};

// Error boundary for QR code
const QRCodeWrapper = ({ value, size = 180 }) => {
  try {
    return (
      <QRCode value={value || "https://example.com"} size={size} level="H" />
    );
  } catch (error) {
    console.error("QR Code error:", error);
    return (
      <div
        className="flex items-center justify-center border-2 border-dashed border-gray-400"
        style={{ width: size, height: size }}
      >
        <p className="text-sm text-muted-foreground">QR Code Error</p>
      </div>
    );
  }
};

export function TVViewOptionsMenu({
  isDark,
  primaryColor,
  onPrayerTimes,
  onMultiday,
  onEvents,
  onImages,
}) {
  return (
    <div className="flex flex-col rounded-xl shadow-xl"
         style={{
           gap: "0.6vh",
           padding: "0.8vh",
           minWidth: "20vh",
           background: isDark ? "rgba(15,15,15,0.96)" : "rgba(255,255,255,0.96)",
           border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.12)",
           backdropFilter: "blur(10px)",
         }}
    >
      {/* Today (navigate to PrayerTimesView) */}
      <div
        onClick={onPrayerTimes}
        className="flex items-center rounded-lg hover:scale-105 transition-all duration-200"
        style={{
          gap: "1.2vh",
          padding: "1.2vh 1.8vh",
          cursor: "pointer",
          background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.03)",
        }}
      >
        <Clock
          style={{
            width: "2.2vh",
            height: "2.2vh",
            color: primaryColor,
          }}
        />
        <span
          className="font-semibold"
          style={{
            fontSize: "2vh",
            color: isDark ? "#fff" : "#1a1a1a",
          }}
        >
          Today
        </span>
      </div>

      {/* Multiday (no right panel) */}
      <div
        onClick={onMultiday}
        className="flex items-center rounded-lg hover:scale-105 transition-all duration-200"
        style={{
          gap: "1.2vh",
          padding: "1.2vh 1.8vh",
          cursor: "pointer",
          background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
        }}
      >
        <CalendarRange
          style={{
            width: "2.2vh",
            height: "2.2vh",
            color: primaryColor,
          }}
        />
        <span
          className="font-semibold"
          style={{
            fontSize: "2vh",
            color: isDark ? "#fff" : "#1a1a1a",
          }}
        >
          Multiday
        </span>
      </div>

      {/* Events */}
      <div
        onClick={onEvents}
        className="flex items-center rounded-lg hover:scale-105 transition-all duration-200"
        style={{
          gap: "1.2vh",
          padding: "1.2vh 1.8vh",
          cursor: "pointer",
          background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
        }}
      >
        <CalendarDays
          style={{
            width: "2.2vh",
            height: "2.2vh",
            color: primaryColor,
          }}
        />
        <span
          className="font-semibold"
          style={{
            fontSize: "2vh",
            color: isDark ? "#fff" : "#1a1a1a",
          }}
        >
          Events
        </span>
      </div>

      {/* Images */}
      <div
        onClick={onImages}
        className="flex items-center rounded-lg hover:scale-105 transition-all duration-200"
        style={{
          gap: "1.2vh",
          padding: "1.2vh 1.8vh",
          cursor: "pointer",
          background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
        }}
      >
        <Camera
          style={{
            width: "2.2vh",
            height: "2.2vh",
            color: primaryColor,
          }}
        />
        <span
          className="font-semibold"
          style={{
            fontSize: "2vh",
            color: isDark ? "#fff" : "#1a1a1a",
          }}
        >
          Images
        </span>
      </div>
    </div>
  );
}

/**
 * PrayerCard - Scalable prayer time display card (MOVED OUTSIDE TeleView to prevent remounting)
 * 
 * SIMPLIFIED SCALING: Uses vh-based units that scale directly with viewport height.
 * This approach is optimized for 16:9 TV displays running fullscreen.
 * 
 * Supports three modes:
 * 1. Regular prayer: Icon + Name header, Athan time (large), Iqama time (small)
 * 2. Jummah with khutbah: Name header, "Khutbah: time", "Prayer: time"
 * 3. Jummah without khutbah: Name header, single prayer time
 */
const PrayerCard = React.memo(function PrayerCard({
  name,
  adhan,
  iqama,
  isCurrent = false,
  isNext = false,
  isJummah = false,
  IconComp = Clock,
  cardSx = {},
  isDark,
  viewMode,
  primaryColor,
  formatCardTime,
  formatTimeNoSuffix,
  getTimeSuffix,
  showTomorrowCards = false,
  isTomorrow = false,
}) {
  // Theme colors - use semi-transparent backgrounds for glass effect
  const currentColor = primaryColor;
  const cardBg = isCurrent
    ? isDark ? `${currentColor}1A` : `${currentColor}20`
    : isNext
      ? isDark ? `${currentColor}0D` : `${currentColor}15`
      : isDark ? "rgba(23, 23, 23, 0.6)" : "rgba(255, 253, 249, 0.7)";
  const borderColor = isCurrent ? currentColor : isDark ? "rgba(255,255,255,0.1)" : "#e8e4dc";
  const mainTextColor = isCurrent ? currentColor : isDark ? "#ffffff" : "#0f172a";
  const mutedTextColor = isCurrent ? currentColor : isDark ? "rgba(255,255,255,0.6)" : "#64748b";

  // Determine content type
  const hasKhutbah = isJummah && adhan;
  const hasIqama = iqama && iqama !== "-";

  // SIMPLIFIED VH-BASED SIZING
  // Base sizes optimized for 16:9 TV at 1080p (1920x1080)
  // Grid is 2 columns x 3 rows, so each card gets ~25vh height with gaps
  // When right panel is shown (viewMode !== "none"), cards are smaller
  // When showTomorrowCards is true, cards are also smaller (4 columns instead of 2)
  
  const hasRightPanel = viewMode && viewMode !== "none";
  const isCompact = hasRightPanel || showTomorrowCards;
  
  // Scale factor based on layout mode (for regular prayer cards only)
  const scaleFactor = isCompact ? 0.72 : 1.0;
  
  // Font sizes - Jummah uses different sizes based on view mode
  // In events/images mode, cards are much narrower so we need smaller text
  let iconSize, nameSize, athanSize, iqamaSize, suffixSize, labelSize;
  
  if (isJummah) {
    if (hasRightPanel) {
      // Events/images view: Jummah cards are narrow (~15-20vw each)
      // Use smaller sizes to prevent overflow
      nameSize = '2.0vh';      // JUMMAH header
      athanSize = '3.0vh';     // Prayer time (no khutbah mode)
      iqamaSize = '3.0vh';     // Khutbah/Prayer times
      suffixSize = '1.8vh';    // AM/PM
      labelSize = '1.5vh';     // "Khutbah"/"Prayer" labels
      iconSize = '2.0vh';      // Not used but keep consistent
    } else {
      // Full-width view (multiday): Use PrayerTimesView-like sizes
      nameSize = '2.5vh';      // JUMMAH header
      athanSize = '3.8vh';     // Prayer time (no khutbah mode)
      iqamaSize = '3.8vh';     // Khutbah/Prayer times
      suffixSize = '2.2vh';    // AM/PM
      labelSize = '1.8vh';     // "Khutbah"/"Prayer" labels
      iconSize = '2.5vh';      // Not used but keep consistent
    }
  } else {
    // Regular prayer card sizes with scale factor
    iconSize = `${3.0 * scaleFactor}vh`;
    nameSize = `${2.6 * scaleFactor}vh`;
    athanSize = `${7.0 * scaleFactor}vh`;
    iqamaSize = `${5.2 * scaleFactor}vh`;
    suffixSize = `${3.2 * scaleFactor}vh`;
    labelSize = `${1.8 * scaleFactor}vh`;
  }

  return (
    <Card
      className="flex flex-col h-full w-full transition-all duration-300"
      style={{
        borderRadius: isJummah ? '1.0vh' : `${1.5 * scaleFactor}vh`,
        borderWidth: isCurrent ? (isJummah ? '0.3vh' : `${0.4 * scaleFactor}vh`) : "2px",
        borderStyle: "solid",
        borderColor,
        background: cardBg,
        boxShadow: isCurrent
          ? isDark ? "0 4px 16px rgba(0,0,0,0.4)" : "0 4px 16px rgba(0,0,0,0.12)"
          : isDark ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.08)",
        minHeight: 0,
        minWidth: 0,
        overflow: "hidden",
        ...cardSx,
      }}
    >
      <CardContent
        className="card-content flex-1 flex flex-col items-center text-center justify-center"
        style={{ 
          padding: isJummah ? '0.5vh 1.0vh' : `${1.0 * scaleFactor}vh`, 
          gap: 0, 
          overflow: "hidden", 
          height: "100%" 
        }}
      >
        <div className="w-full h-full flex items-center justify-center">
          <div
            className="flex flex-col items-center justify-center"
            style={{
              gap: isJummah ? '0.3vh' : `${0.5 * scaleFactor}vh`,
            }}
          >
            {/* Prayer name header with icon */}
            {!isJummah && (
              <div className="flex items-center justify-center" style={{ gap: `${0.8 * scaleFactor}vh` }}>
                <IconComp style={{ width: iconSize, height: iconSize, color: mutedTextColor }} />
                <span className="font-semibold whitespace-nowrap" style={{ fontSize: nameSize, color: mutedTextColor }}>
                  {name}
                </span>
              </div>
            )}

            {/* Time display */}
            {isJummah && hasKhutbah ? (
              // Jummah WITH khutbah: Header on top, then Khutbah row, then Prayer row
              <div className="flex flex-col items-center" style={{ gap: '0.2vh' }}>
                {/* Jummah Header */}
                <span 
                  className="font-bold uppercase tracking-wider" 
                  style={{ fontSize: nameSize, color: mutedTextColor }}
                >
                  {name}
                </span>
                {/* Khutbah row */}
                <div className="flex items-baseline" style={{ gap: '0.8vw' }}>
                  <span className="flex items-baseline">
                    <span 
                      className="font-sans font-bold leading-none whitespace-nowrap" 
                      style={{ fontSize: iqamaSize, color: mainTextColor, fontVariantNumeric: 'tabular-nums' }}
                    >
                      {formatTimeNoSuffix(adhan, name)}
                    </span>
                    <span 
                      className="font-medium whitespace-nowrap" 
                      style={{ fontSize: suffixSize, color: mutedTextColor }}
                    >
                      {getTimeSuffix(adhan, name)}
                    </span>
                  </span>
                  <span 
                    className="font-bold uppercase tracking-wider whitespace-nowrap" 
                    style={{ fontSize: labelSize, color: mutedTextColor }}
                  >
                    Khutbah
                  </span>
                </div>
                {/* Prayer row */}
                <div className="flex items-baseline" style={{ gap: '0.8vw' }}>
                  <span className="flex items-baseline">
                    <span 
                      className="font-sans font-bold leading-none whitespace-nowrap" 
                      style={{ fontSize: iqamaSize, color: primaryColor, fontVariantNumeric: 'tabular-nums' }}
                    >
                      {formatTimeNoSuffix(iqama, name)}
                    </span>
                    <span 
                      className="font-medium whitespace-nowrap" 
                      style={{ fontSize: suffixSize, color: `${primaryColor}cc` }}
                    >
                      {getTimeSuffix(iqama, name)}
                    </span>
                  </span>
                  <span 
                    className="font-bold uppercase tracking-wider whitespace-nowrap" 
                    style={{ fontSize: labelSize, color: mutedTextColor }}
                  >
                    Prayer
                  </span>
                </div>
              </div>
            ) : isJummah ? (
              // Jummah WITHOUT khutbah: Header + single Prayer row
              <div className="flex flex-col items-center" style={{ gap: '0.3vh' }}>
                {/* Jummah Header */}
                <span 
                  className="font-bold uppercase tracking-wider" 
                  style={{ fontSize: nameSize, color: mutedTextColor }}
                >
                  {name}
                </span>
                {/* Prayer row */}
                <div className="flex items-baseline" style={{ gap: '0.8vw' }}>
                  <span className="flex items-baseline">
                    <span 
                      className="font-sans font-bold leading-none whitespace-nowrap" 
                      style={{ fontSize: athanSize, color: primaryColor, fontVariantNumeric: 'tabular-nums' }}
                    >
                      {formatTimeNoSuffix(hasIqama ? iqama : adhan, name)}
                    </span>
                    <span 
                      className="font-medium whitespace-nowrap" 
                      style={{ fontSize: suffixSize, color: `${primaryColor}cc` }}
                    >
                      {getTimeSuffix(hasIqama ? iqama : adhan, name)}
                    </span>
                  </span>
                  <span 
                    className="font-bold uppercase tracking-wider whitespace-nowrap" 
                    style={{ fontSize: labelSize, color: mutedTextColor }}
                  >
                    Prayer
                  </span>
                </div>
              </div>
            ) : (
              // Regular prayer: Athan time (large) + Iqama time (smaller, highlighted)
              <div className="flex flex-col items-center" style={{ gap: `${0.3 * scaleFactor}vh` }}>
                {/* Adhan time - primary, large */}
                <div className="flex items-baseline" style={{ gap: `${0.4 * scaleFactor}vh` }}>
                  <span 
                    className="font-sans leading-none whitespace-nowrap tabular-nums" 
                    style={{ 
                      fontSize: athanSize, 
                      fontWeight: isCurrent ? 700 : 600, 
                      color: mainTextColor,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatTimeNoSuffix(adhan, name)}
                  </span>
                  <span 
                    className="font-medium whitespace-nowrap" 
                    style={{ fontSize: suffixSize, color: mutedTextColor }}
                  >
                    {getTimeSuffix(adhan, name)}
                  </span>
                </div>
                {/* Iqama time - secondary, highlighted with primary color (not for tomorrow) */}
                {hasIqama && (
                  <div className="flex items-baseline" style={{ gap: `${0.3 * scaleFactor}vh` }}>
                    <span 
                      className="font-sans font-bold leading-none whitespace-nowrap tabular-nums" 
                      style={{ 
                        fontSize: iqamaSize, 
                        color: isTomorrow ? mainTextColor : primaryColor,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {formatTimeNoSuffix(iqama, name)}
                    </span>
                    <span 
                      className="font-medium whitespace-nowrap" 
                      style={{ fontSize: `${2.6 * scaleFactor}vh`, color: isTomorrow ? mutedTextColor : `${primaryColor}cc` }}
                    >
                      {getTimeSuffix(iqama, name)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

export default function TeleView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { appearance, theme } = useAppTheme();

  const [masjidData, setMasjidData] = useState(null);
  const [todayPrayerTimes, setTodayPrayerTimes] = useState(null);
  const [tomorrowPrayerTimes, setTomorrowPrayerTimes] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  // Initialize fullscreen state from actual document state
  const [isFullscreen, setIsFullscreen] = useState(() => !!document.fullscreenElement);
  const [controlsVisible, setControlsVisible] = useState(true);
  const inactivityTimeoutRef = useRef(null);
  const wasFullscreenRef = useRef(false); // Track if we were in fullscreen before ESC (for Firefox workaround)
  const [currentEventIndex, setCurrentEventIndex] = useState(0);

  // New states for slideshow
  // Initialize viewMode from location.state, or from localStorage, or default to "none"
  const getInitialViewMode = () => {
    if (location.state?.viewMode) {
      return location.state.viewMode;
    }
    // Map localStorage tvSettings viewMode to TeleView viewMode
    const storedViewMode = localStorage.getItem("tv-view-mode");
    const mapping = {
      [VIEW_MODES.MULTIDAY]: "none",
      [VIEW_MODES.EVENTS]: "events",
      [VIEW_MODES.IMAGES]: "images",
    };
    return mapping[storedViewMode] || "none";
  };
  const [viewMode, setViewMode] = useState(getInitialViewMode);
  const [viewSwitching, setViewSwitching] = useState(false);
  const viewModeRef = useRef(viewMode);
  const remoteSettingsSyncRef = useRef(0);
  const remoteViewSyncRef = useRef(0);

  useEffect(() => {
  }, [location.pathname]);
  
  useEffect(() => {
  }, [viewMode]);

  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);
  
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [sliderNoTransition, setSliderNoTransition] = useState(false);
  const [imageRefreshNonce, setImageRefreshNonce] = useState(0);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [masjidId, setMasjidId] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [squareSize, setSquareSize] = useState("100vh");
  const [sizeMultiplier, setSizeMultiplier] = useState(1.0);
  const [themeMode, setThemeMode] = useState(
    () => localStorage.getItem("tv-theme-mode") || "manual"
  );
  // Local appearance state for this view only
  const [localAppearance, setLocalAppearance] = useState(appearance);
  const [themeModeMenuOpen, setThemeModeMenuOpen] = useState(false);
  const [zoomMenuOpen, setZoomMenuOpen] = useState(false);

  // Primary color: managed by tvSettings hook (loaded from localStorage)
  const [primaryColor, setPrimaryColor] = useState(theme.colors.primary);
  // SSE Remote Control state
  const [sseConnected, setSseConnected] = useState(false);

  // Settings dialog state
  const [settingsOpen, setSettingsOpen] = useState(false);

  // TV Settings hook for shared settings management
  const tvSettings = useTVSettings({ globalTheme: theme, globalAppearance: appearance });

  // Sync tvSettings with local state
  // These effects ensure the hook's settings drive the component's behavior
  useEffect(() => {
    setThemeMode(tvSettings.themeMode);
  }, [tvSettings.themeMode]);

  useEffect(() => {
    setLocalAppearance(tvSettings.localAppearance);
  }, [tvSettings.localAppearance]);

  useEffect(() => {
    setPrimaryColor(tvSettings.primaryColor);
  }, [tvSettings.primaryColor]);

  useEffect(() => {
    setSizeMultiplier(tvSettings.zoomLevel);
  }, [tvSettings.zoomLevel]);

  // Note: showTomorrowCards is managed by the layout evaluation effect below
  // which already respects tvSettings.showTomorrow when there's enough space

  // Sync viewMode with tvSettings.viewMode (on mount and when tvSettings changes)
  useEffect(() => {
    // Map tvSettings viewMode to TeleView viewMode
    // today -> handled by onViewChange (navigates away)
    // multiday -> "none"
    // events -> "events"
    // images -> "images"
    const mapping = {
      [VIEW_MODES.MULTIDAY]: "none",
      [VIEW_MODES.EVENTS]: "events",
      [VIEW_MODES.IMAGES]: "images",
    };
    const newViewMode = mapping[tvSettings.viewMode];
    if (newViewMode && newViewMode !== viewMode) {
      setViewMode(newViewMode);
    }
  }, [tvSettings.viewMode]);

  // Sync TeleView viewMode changes back to tvSettings
  useEffect(() => {
    // Map TeleView viewMode to tvSettings viewMode
    const mapping = {
      "none": VIEW_MODES.MULTIDAY,
      "events": VIEW_MODES.EVENTS,
      "images": VIEW_MODES.IMAGES,
    };
    const tvSettingsViewMode = mapping[viewMode];
    if (tvSettingsViewMode && tvSettingsViewMode !== tvSettings.viewMode) {
      tvSettings.setViewMode(tvSettingsViewMode);
    }
  }, [viewMode, tvSettings.viewMode, tvSettings.setViewMode]);

  // Test mode state (for overlay test buttons)
  const [testMode, setTestMode] = useState(null); // null | "adhan" | "iqama" | "jummah" | "sunrise"
  const testTimerRef = useRef(null);

  // Countdown overlay hook for adhan/iqama/jummah/forbidden announcements
  const countdownOverlay = useCountdownOverlay({
    todayPrayerTimes,
    tomorrowPrayerTimes,
    testMode: Boolean(testMode),
    tvSettings,
    paused: settingsOpen,
  });

  // Destructure setters for test mode
  const {
    setOverlayMode,
    setOverlayPrayerName,
    setOverlayType,
    setOverlayCountdownSeconds,
    setScreenDimmed,
    setIqamaExtraDimmed,
    clearOverlay,
  } = countdownOverlay;

  // ESC key handler to dismiss overlays (prevents exiting fullscreen when overlay is active)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && countdownOverlay.overlayMode) {
        // Prevent default ESC behavior (exiting fullscreen) when overlay is showing
        e.preventDefault();
        e.stopPropagation();
        // Clear test mode if active
        if (testMode) {
          if (testTimerRef.current) clearInterval(testTimerRef.current);
          setTestMode(null);
        }
        clearOverlay(true); // true = manual dismiss, prevents re-triggering for 5 minutes
      }
    };

    // Use capture phase to intercept ESC before browser handles fullscreen exit
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [countdownOverlay.overlayMode, testMode, clearOverlay]);

  // Report overlay state changes to SSE so app remote can show/hide dismiss button
  useEffect(() => {
    const overlayActive = Boolean(countdownOverlay.overlayMode);
    updateTvOverlayState(overlayActive);
  }, [countdownOverlay.overlayMode]);

  // Generate a friendly device name from user agent
  const getDeviceName = () => {
    const ua = navigator.userAgent;
    let browser = "Browser";
    let os = "Device";

    // Detect browser
    if (ua.includes("Chrome") && !ua.includes("Edg")) browser = "Chrome";
    else if (ua.includes("Safari") && !ua.includes("Chrome")) browser = "Safari";
    else if (ua.includes("Firefox")) browser = "Firefox";
    else if (ua.includes("Edg")) browser = "Edge";

    // Detect OS
    if (ua.includes("Mac")) os = "Mac";
    else if (ua.includes("Windows")) os = "Windows";
    else if (ua.includes("Linux")) os = "Linux";
    else if (ua.includes("Android")) os = "Android";
    else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";

    return `${browser} on ${os}`;
  };

  // Get or create a session ID for this browser tab (persists across view changes)
  const getSessionId = () => {
    let sessionId = sessionStorage.getItem("tv-session-id");
    if (!sessionId) {
      sessionId = Math.random().toString(36).substring(2, 10);
      sessionStorage.setItem("tv-session-id", sessionId);
    }
    return sessionId;
  };

  // Load primary color override from localStorage if present
  // This allows TV view to use a custom accent independent of theme
  // If no override, we keep it in sync with the active theme
  

  // Responsive visibility states - spaceTooSmall, showJummahAndQR, showAyah are computed by effect
  // showTomorrowCards is computed directly from settings + data availability

  // Ayah card state: daily-random selection, allow manual navigation when menu is open
  const [ayahIndex, setAyahIndex] = useState(0);

  // Update square size based on multiplier
  useEffect(() => {
    const baseSize = 100; // 100vh
    const newSize = baseSize * sizeMultiplier;
    setSquareSize(`${newSize}vh`);
  }, [sizeMultiplier]);

  // Primary color is managed by tvSettings hook (which loads from localStorage)
  // No need for separate localStorage loading as tvSettings handles persistence

  // Note: Font scaling removed - was causing issues with dialogs and rem-based UI.
  // TeleView now uses standard 16px base font size like the rest of the app.

  // Calculate square size based on viewport - shrink at lower resolutions to give more space to prayer times
  useEffect(() => {
    const calculateSquareSize = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // At 1920px+ width: use 100% of vh (full square)
      // At 1280px width: use 55% of vh (smaller panel, more space for prayers)
      // Linear interpolation between these breakpoints
      const minWidth = 1280;
      const maxWidth = 1920;
      const minPercent = 0.55;
      const maxPercent = 1.0;

      let percent;
      if (vw >= maxWidth) {
        percent = maxPercent;
      } else if (vw <= minWidth) {
        percent = minPercent;
      } else {
        // Linear interpolation
        const ratio = (vw - minWidth) / (maxWidth - minWidth);
        percent = minPercent + ratio * (maxPercent - minPercent);
      }

      const size = vh * percent;
      setSquareSize(`${size}px`);
    };

    calculateSquareSize();
    window.addEventListener("resize", calculateSquareSize);
    return () => window.removeEventListener("resize", calculateSquareSize);
  }, [isFullscreen]);

  // pick a deterministic random ayah index per-day so it changes daily
  // only run when the day string changes (so we don't override manual changes while the clock ticks)
  const currentDay = currentDate.toISOString().slice(0, 10); // YYYY-MM-DD
  useEffect(() => {
    try {
      const count = Array.isArray(ayatData) ? ayatData.length : 0;
      if (count > 0) {
        const d = currentDay;
        let h = 0;
        for (let i = 0; i < d.length; i++) {
          h = (h * 31 + d.charCodeAt(i)) >>> 0;
        }
        setAyahIndex(h % count);
      }
    } catch (e) {
      // ignore
    }
  }, [currentDay]);

  // Auto theme switching based on sunrise/maghrib (adhan times)
  const lastTVDesiredRef = useRef(null);
  useEffect(() => {
    // Handle non-auto modes or when we don't have prayer times
    if (tvSettings.themeMode !== "auto") {
      lastTVDesiredRef.current = null;
      return;
    }
    
    // For AUTO mode, compute appearance based on sunrise/maghrib
    if (!todayPrayerTimes) {
      // No prayer times yet, use a sensible default based on current hour
      const hour = currentDate.getHours();
      const defaultAppearance = (hour >= 18 || hour < 6) ? "dark" : "light";
      if (lastTVDesiredRef.current !== defaultAppearance) {
        lastTVDesiredRef.current = defaultAppearance;
        setLocalAppearance(defaultAppearance);
        tvSettings.setLocalAppearance(defaultAppearance);
      }
      return;
    }
    
    const sunriseStr = todayPrayerTimes?.sunrise;
    const maghribData = todayPrayerTimes?.maghrib;
    const maghribStr = maghribData?.adhan || maghribData;
    const sunrise = parseTimeStringToToday(sunriseStr, currentDate, "sunrise");
    const maghrib = parseTimeStringToToday(maghribStr, currentDate, "maghrib");
    const now = currentDate;
    if (sunrise && maghrib) {
      const desiredAppearance = now >= maghrib || now < sunrise ? "dark" : "light";
      if (lastTVDesiredRef.current !== desiredAppearance) {
        lastTVDesiredRef.current = desiredAppearance;
        setLocalAppearance(desiredAppearance);
        // Also update tvSettings so the dialog knows the current appearance
        tvSettings.setLocalAppearance(desiredAppearance);
      }
    }
  }, [tvSettings.themeMode, todayPrayerTimes, currentDate, tvSettings]);

  // Helper to reset zoom
  const resetZoom = () => setSizeMultiplier(1.0);

  // Persist theme mode changes and update local appearance
  useEffect(() => {
    localStorage.setItem("tv-theme-mode", themeMode);
    if (themeMode === "light") {
      setLocalAppearance("light");
    } else if (themeMode === "dark") {
      setLocalAppearance("dark");
    }
  }, [themeMode]);

  // Update date every second
  useEffect(() => {
    const interval = setInterval(() => setCurrentDate(new Date()), 500);
    return () => clearInterval(interval);
  }, []);

  // Fullscreen detection with Firefox workaround
  // Firefox exits fullscreen on ESC before JS can prevent it, so we re-enter if overlay was showing
  useEffect(() => {
    const handleFullscreenChange = async () => {
      const nowFullscreen = !!document.fullscreenElement;
      const wasFullscreen = wasFullscreenRef.current;
      
      // If we just exited fullscreen while an overlay was showing, re-enter fullscreen
      // This handles Firefox which doesn't allow preventing ESC from exiting fullscreen
      if (wasFullscreen && !nowFullscreen && countdownOverlay.overlayMode) {
        // Clear the overlay first (user wanted to dismiss it with ESC)
        if (testMode) {
          if (testTimerRef.current) clearInterval(testTimerRef.current);
          setTestMode(null);
        }
        clearOverlay();
        
        // Re-enter fullscreen after a brief delay
        setTimeout(async () => {
          try {
            const element = document.documentElement;
            if (element.requestFullscreen) await element.requestFullscreen();
          } catch (err) {
            // User may have navigated away or browser blocked it
            console.log("Could not re-enter fullscreen:", err);
          }
        }, 50);
      }
      
      wasFullscreenRef.current = nowFullscreen;
      setIsFullscreen(nowFullscreen);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange
      );
    };
  }, [countdownOverlay.overlayMode, testMode, clearOverlay]);

  // Auto-hide controls when mouse is inactive (double timeout; don't hide if image dialog open)
  useEffect(() => {
    const resetTimer = () => {
      if (inactivityTimeoutRef.current)
        clearTimeout(inactivityTimeoutRef.current);
      setControlsVisible(true);
      inactivityTimeoutRef.current = setTimeout(() => {
        if (showImageDialog) return; // keep visible while managing images
        // Hide controls after timeout in all modes (including fullscreen)
        setControlsVisible(false);
        // Also close any open menus when controls hide
        setMenuOpen(false);
        setThemeModeMenuOpen(false);
        setZoomMenuOpen(false);
      }, 2000);
    };

    const onActivity = () => resetTimer();
    window.addEventListener("mousemove", onActivity);
    window.addEventListener("touchstart", onActivity);
    window.addEventListener("keydown", onActivity);

    // start timer
    resetTimer();

    return () => {
      window.removeEventListener("mousemove", onActivity);
      window.removeEventListener("touchstart", onActivity);
      window.removeEventListener("keydown", onActivity);
      if (inactivityTimeoutRef.current)
        clearTimeout(inactivityTimeoutRef.current);
    };
  }, [showImageDialog]);

  // Exit fullscreen with Escape
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && document.fullscreenElement)
        exitFullscreen();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const enterFullscreen = async () => {
    try {
      const element = document.documentElement;
      if (element.requestFullscreen) await element.requestFullscreen();
      else if (element.webkitRequestFullscreen)
        await element.webkitRequestFullscreen();
      else if (element.mozRequestFullScreen)
        await element.mozRequestFullScreen();
      else if (element.msRequestFullscreen) await element.msRequestFullscreen();
    } catch (err) {
      console.error("Error entering fullscreen:", err);
    }
  };

  const exitFullscreen = async () => {
    try {
      if (document.exitFullscreen) await document.exitFullscreen();
      else if (document.webkitExitFullscreen)
        await document.webkitExitFullscreen();
      else if (document.mozCancelFullScreen)
        await document.mozCancelFullScreen();
      else if (document.msExitFullscreen) await document.msExitFullscreen();
    } catch (err) {
      console.error("Error exiting fullscreen:", err);
    }
  };

  const handleFullscreenToggle = () =>
    isFullscreen ? exitFullscreen() : enterFullscreen();

  // Note: Automatic fullscreen restoration removed.
  // Browsers require user interaction to enter fullscreen mode.
  // Users can manually enter fullscreen using the fullscreen button or F11.

  // Only exit fullscreen if explicitly navigating away via back button
  useEffect(() => {
    return () => {
      const shouldExitFullscreen = sessionStorage.getItem('exit-fullscreen') === 'true';
      sessionStorage.removeItem('exit-fullscreen');
      
      if (document.fullscreenElement && shouldExitFullscreen) {
        exitFullscreen();
      } else if (document.fullscreenElement) {
        // Store fullscreen state for next view to restore
        sessionStorage.setItem('tv-fullscreen-state', 'true');
      }
    };
  }, []);

  const buildPrayerTimesNavState = () => {
    if (!masjidId) return null;
    return {
      masjidId,
      preload: {
        masjidData: masjidData || null,
        todayPrayerTimes: todayPrayerTimes || null,
        tomorrowPrayerTimes: tomorrowPrayerTimes || null,
      },
    };
  };

  const navigateToPrayerTimesView = () => {
    const state = buildPrayerTimesNavState();
    navigate(
      "/admin/prayer-times-view",
      state ? { state } : undefined
    );
  };

  // View switching handlers for config menu
  const handleSelectPrayerTimes = () => {
    // Navigate to single-day view (PrayerTimesView)
    setMenuOpen(false);
    navigateToPrayerTimesView();
  };
  const handleSelectMultiday = () => {
    setViewMode("none");
    setMenuOpen(false);
  };
  const handleSelectEvents = () => {
    setViewMode("events");
    setMenuOpen(false);
  };
  const handleSelectImages = () => {
    setViewMode("images");
    setMenuOpen(false);
  };

  // Load masjid, events, prayer times
  useEffect(() => {
    const loadData = async () => {
      if (authLoading) return;
      if (!user?.email) {
        setError("Please log in to view this page");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const adminData = await checkAdmin(user.email);
        if (!adminData.isAdmin) {
          setError("You do not have admin privileges");
          setLoading(false);
          return;
        }

        // Track if user is super admin for back navigation
        setIsSuperAdmin(adminData.role === "super");

        const masjidIdValue =
          adminData.role === "super"
            ? adminData.activeMasjidId
            : adminData.masjidId;
        if (!masjidIdValue) {
          setError("No masjid assigned. Please contact system administrator.");
          setLoading(false);
          return;
        }

        setMasjidId(masjidIdValue);

        // Load TV data from API (masjid + prayer times)
        const tvData = await getTvData(masjidIdValue);
        if (tvData.ok) {
          setMasjidData(tvData.masjid);
          setTodayPrayerTimes(tvData.todayPrayerTimes);
          setTomorrowPrayerTimes(tvData.tomorrowPrayerTimes);
        }

        await loadEvents(masjidIdValue);
      } catch (err) {
        console.error("Error loading data:", err);
        setError("Failed to load data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Refresh prayer times and events every 5 minutes
    const refreshInterval = setInterval(() => {
      if (masjidId) {
        console.log("Refreshing data...");
        loadPrayerTimes(masjidId);
        loadEvents(masjidId);
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, [user, authLoading, masjidId]);

  // Live remote control (Pro masjids only). Non-Pro skip the listener entirely;
  // the TV display still works, just without remote control.
  useEffect(() => {
    if (!masjidId || !user) return;
    if (masjidData?.subscription !== "pro") return;

    const deviceName = getDeviceName();
    const sessionId = getSessionId();
    const initialView = viewMode === "none" ? "multiday" : viewMode;
    // Accept forceRefresh param for reconnection scenarios where token may have expired
    const getToken = (forceRefresh = false) => auth.currentUser?.getIdToken(forceRefresh);

    acquireTvSse({
      masjidId,
      masjidApi: MASJID_API,
      sessionId,
      deviceName,
      initialView,
      getToken,
      // Send current TV settings for app remote to sync
      themeMode: tvSettings.themeMode,
      primaryColor: tvSettings.primaryColor,
      showTomorrow: tvSettings.showTomorrow,
      zoomLevel: tvSettings.zoomLevel,
      adhanEnabled: tvSettings.adhanEnabled,
      iqamaEnabled: tvSettings.iqamaEnabled,
      adhanCountdownMinutes: tvSettings.adhanCountdownMinutes,
      iqamaCountdownMinutes: tvSettings.iqamaCountdownMinutes,
      adhanScreenDurationMinutes: tvSettings.adhanScreenDurationMinutes,
      iqamaScreenDurationMinutes: tvSettings.iqamaScreenDurationMinutes,
      jummahSilenceEnabled: tvSettings.jummahSilenceEnabled,
      jummahSilenceDurationMinutes: tvSettings.jummahSilenceDurationMinutes,
      forbiddenScreenEnabled: tvSettings.forbiddenScreenEnabled,
      forbiddenScreenDurationMinutes: tvSettings.forbiddenScreenDurationMinutes,
      tomorrowChangesBanner: tvSettings.tomorrowChangesBanner,
    });

    const unsubscribeStatus = addTvStatusListener((status) => {
      setSseConnected(Boolean(status.connected));
      if (status.clientId) {
        const currentView = viewModeRef.current === "none" ? "multiday" : viewModeRef.current;
        updateTvLastView(currentView);
      }
    });

    const unsubscribeCommand = addTvCommandListener((data) => {
      if (REMOTE_SETTINGS_COMMANDS.has(data.command)) {
        remoteSettingsSyncRef.current = Date.now();
      }
      if (data.command === "changeView") {
        remoteViewSyncRef.current = Date.now();
        const targetView = data.view;

        // Show loading state
        setViewSwitching(true);

        // Handle view changes
        if (targetView === "prayer-times" || targetView === "today") {
          setTimeout(() => {
            navigateToPrayerTimesView();
          }, 100);
        } else if (targetView === "teleview" || targetView === "multiday") {
          // Stay on TeleView with no panel
          setViewMode("none");
          // Hide loading after brief delay
          setTimeout(() => setViewSwitching(false), 500);
        } else if (targetView === "events") {
          setViewMode("events");
          setTimeout(() => setViewSwitching(false), 500);
        } else if (targetView === "images") {
          setViewMode("images");
          setTimeout(() => setViewSwitching(false), 500);
        } else {
          // Unknown view, hide loading
          setViewSwitching(false);
        }
      }

      // Handle theme change command (legacy - simple dark/light toggle)
      if (data.command === "changeTheme" && data.theme) {
        if (data.theme === "light") {
          tvSettings.setThemeMode("light");
        } else if (data.theme === "dark") {
          tvSettings.setThemeMode("dark");
        } else if (data.theme === "auto") {
          tvSettings.setThemeMode("auto");
        }
      }

      // Handle changeTheme with extended settings (from mobile remote)
      if (data.command === "changeTheme") {
        if (data.themeMode) tvSettings.setThemeMode(data.themeMode);
        if (data.primaryColor) tvSettings.setPrimaryColor(data.primaryColor);
        if (data.showTomorrow !== undefined && data.showTomorrow !== null) tvSettings.setShowTomorrow(data.showTomorrow);
        if (data.zoomLevel !== undefined && data.zoomLevel !== null) tvSettings.setZoomLevel(data.zoomLevel);
        if (data.adhanEnabled !== undefined && data.adhanEnabled !== null) tvSettings.setAdhanEnabled(data.adhanEnabled);
        if (data.iqamaEnabled !== undefined && data.iqamaEnabled !== null) tvSettings.setIqamaEnabled(data.iqamaEnabled);
        if (data.adhanCountdownMinutes !== undefined && data.adhanCountdownMinutes !== null) tvSettings.setAdhanCountdownMinutes(data.adhanCountdownMinutes);
        if (data.iqamaCountdownMinutes !== undefined && data.iqamaCountdownMinutes !== null) tvSettings.setIqamaCountdownMinutes(data.iqamaCountdownMinutes);
        if (data.adhanScreenDurationMinutes !== undefined && data.adhanScreenDurationMinutes !== null) tvSettings.setAdhanScreenDurationMinutes(data.adhanScreenDurationMinutes);
        if (data.iqamaScreenDurationMinutes !== undefined && data.iqamaScreenDurationMinutes !== null) tvSettings.setIqamaScreenDurationMinutes(data.iqamaScreenDurationMinutes);
        // Jummah silence settings
        if (data.jummahSilenceEnabled !== undefined && data.jummahSilenceEnabled !== null) tvSettings.setJummahSilenceEnabled(data.jummahSilenceEnabled);
        if (data.jummahSilenceDurationMinutes !== undefined && data.jummahSilenceDurationMinutes !== null) tvSettings.setJummahSilenceDurationMinutes(data.jummahSilenceDurationMinutes);
        // Forbidden screen settings
        if (data.forbiddenScreenEnabled !== undefined && data.forbiddenScreenEnabled !== null) tvSettings.setForbiddenScreenEnabled(data.forbiddenScreenEnabled);
        if (data.forbiddenScreenDurationMinutes !== undefined && data.forbiddenScreenDurationMinutes !== null) tvSettings.setForbiddenScreenDurationMinutes(data.forbiddenScreenDurationMinutes);
        // Tomorrow changes banner
        if (data.tomorrowChangesBanner !== undefined && data.tomorrowChangesBanner !== null) tvSettings.setTomorrowChangesBanner(data.tomorrowChangesBanner);
      }

      // Handle resetSettings command (reset all to defaults)
      if (data.command === "resetSettings") {
        tvSettings.resetToDefaults();
      }

      // Handle theme mode setting (extended - light/dark/system/auto)
      if (data.command === "setThemeMode" && data.themeMode) {
        tvSettings.setThemeMode(data.themeMode);
      }

      // Handle primary color setting
      if (data.command === "setPrimaryColor" && data.primaryColor) {
        tvSettings.setPrimaryColor(data.primaryColor);
      }

      // Handle custom color setting
      if (data.command === "setCustomColor" && data.primaryColor) {
        tvSettings.setCustomColor(data.primaryColor);
      }

      // Handle show tomorrow setting
      if (data.command === "setShowTomorrow" && data.showTomorrow !== undefined) {
        tvSettings.setShowTomorrow(data.showTomorrow);
      }

      // Handle zoom level setting (direct value)
      if (data.command === "setZoomLevel" && data.zoomLevel !== undefined) {
        tvSettings.setZoomLevel(data.zoomLevel);
      }

      // Handle adhan/iqama enabled settings
      if (data.command === "setAdhanEnabled" && data.adhanEnabled !== undefined) {
        tvSettings.setAdhanEnabled(data.adhanEnabled);
      }
      if (data.command === "setIqamaEnabled" && data.iqamaEnabled !== undefined) {
        tvSettings.setIqamaEnabled(data.iqamaEnabled);
      }

      // Handle countdown minute settings
      if (data.command === "setAdhanCountdownMinutes" && data.adhanCountdownMinutes !== undefined) {
        tvSettings.setAdhanCountdownMinutes(data.adhanCountdownMinutes);
      }
      if (data.command === "setIqamaCountdownMinutes" && data.iqamaCountdownMinutes !== undefined) {
        tvSettings.setIqamaCountdownMinutes(data.iqamaCountdownMinutes);
      }
      if (data.command === "setIqamaScreenDurationMinutes" && data.iqamaScreenDurationMinutes !== undefined) {
        tvSettings.setIqamaScreenDurationMinutes(data.iqamaScreenDurationMinutes);
      }
      if (data.command === "setAdhanScreenDurationMinutes" && data.adhanScreenDurationMinutes !== undefined) {
        tvSettings.setAdhanScreenDurationMinutes(data.adhanScreenDurationMinutes);
      }

      // Handle jummah silence settings
      if (data.command === "setJummahSilenceEnabled" && data.jummahSilenceEnabled !== undefined) {
        tvSettings.setJummahSilenceEnabled(data.jummahSilenceEnabled);
      }
      if (data.command === "setJummahSilenceDurationMinutes" && data.jummahSilenceDurationMinutes !== undefined) {
        tvSettings.setJummahSilenceDurationMinutes(data.jummahSilenceDurationMinutes);
      }

      // Handle forbidden screen settings
      if (data.command === "setForbiddenScreenEnabled" && data.forbiddenScreenEnabled !== undefined) {
        tvSettings.setForbiddenScreenEnabled(data.forbiddenScreenEnabled);
      }
      if (data.command === "setForbiddenScreenDurationMinutes" && data.forbiddenScreenDurationMinutes !== undefined) {
        tvSettings.setForbiddenScreenDurationMinutes(data.forbiddenScreenDurationMinutes);
      }

      // Handle bulk settings update (update multiple settings at once)
      if (data.command === "updateSettings") {
        if (data.themeMode) tvSettings.setThemeMode(data.themeMode);
        if (data.primaryColor) tvSettings.setPrimaryColor(data.primaryColor);
        if (data.showTomorrow !== undefined && data.showTomorrow !== null) tvSettings.setShowTomorrow(data.showTomorrow);
        if (data.zoomLevel !== undefined && data.zoomLevel !== null) tvSettings.setZoomLevel(data.zoomLevel);
        if (data.adhanEnabled !== undefined && data.adhanEnabled !== null) tvSettings.setAdhanEnabled(data.adhanEnabled);
        if (data.iqamaEnabled !== undefined && data.iqamaEnabled !== null) tvSettings.setIqamaEnabled(data.iqamaEnabled);
        if (data.adhanCountdownMinutes !== undefined && data.adhanCountdownMinutes !== null) tvSettings.setAdhanCountdownMinutes(data.adhanCountdownMinutes);
        if (data.iqamaCountdownMinutes !== undefined && data.iqamaCountdownMinutes !== null) tvSettings.setIqamaCountdownMinutes(data.iqamaCountdownMinutes);
        if (data.adhanScreenDurationMinutes !== undefined && data.adhanScreenDurationMinutes !== null) tvSettings.setAdhanScreenDurationMinutes(data.adhanScreenDurationMinutes);
        if (data.iqamaScreenDurationMinutes !== undefined && data.iqamaScreenDurationMinutes !== null) tvSettings.setIqamaScreenDurationMinutes(data.iqamaScreenDurationMinutes);
        // Jummah silence settings
        if (data.jummahSilenceEnabled !== undefined && data.jummahSilenceEnabled !== null) tvSettings.setJummahSilenceEnabled(data.jummahSilenceEnabled);
        if (data.jummahSilenceDurationMinutes !== undefined && data.jummahSilenceDurationMinutes !== null) tvSettings.setJummahSilenceDurationMinutes(data.jummahSilenceDurationMinutes);
        // Forbidden screen settings
        if (data.forbiddenScreenEnabled !== undefined && data.forbiddenScreenEnabled !== null) tvSettings.setForbiddenScreenEnabled(data.forbiddenScreenEnabled);
        if (data.forbiddenScreenDurationMinutes !== undefined && data.forbiddenScreenDurationMinutes !== null) tvSettings.setForbiddenScreenDurationMinutes(data.forbiddenScreenDurationMinutes);
        // Tomorrow changes banner
        if (data.tomorrowChangesBanner !== undefined && data.tomorrowChangesBanner !== null) tvSettings.setTomorrowChangesBanner(data.tomorrowChangesBanner);
      }

      // Handle refresh command
      if (data.command === "refresh") {
        // Set flag to enter fullscreen after reload
        sessionStorage.setItem("tv-enter-fullscreen-after-refresh", "true");
        window.location.reload();
      }

      // Handle dismiss overlay command
      if (data.command === "dismissOverlay") {
        // Clear test mode if active
        if (testTimerRef.current) {
          clearInterval(testTimerRef.current);
          testTimerRef.current = null;
        }
        setTestMode(null);
        clearOverlay();
      }

      // Handle refreshImages command - reload images from backend
      if (data.command === "refreshImages") {
        setImageRefreshNonce((prev) => prev + 1);
      }

      // Handle zoom commands - only when in events or images view mode
      if (
        data.command === "zoomIn" ||
        data.command === "zoomOut" ||
        data.command === "resetZoom"
      ) {
        // Get current viewMode from state (use functional update pattern to access latest)
        setViewMode((currentViewMode) => {
          if (currentViewMode === "events" || currentViewMode === "images") {
            if (data.command === "zoomIn") {
              tvSettings.setZoomLevel(tvSettings.zoomLevel + 0.1);
            } else if (data.command === "zoomOut") {
              tvSettings.setZoomLevel(tvSettings.zoomLevel - 0.1);
            } else if (data.command === "resetZoom") {
              tvSettings.setZoomLevel(1.0);
            }
          }
          return currentViewMode; // Return unchanged to avoid state update
        });
      }

      // Handle prayer times updated command (from admin changing times)
      if (data.command === "prayerTimesUpdated") {
        console.log("[TV] Prayer times updated, reloading data...");
        loadPrayerTimes(masjidId);
      }
    });

    return () => {
      unsubscribeStatus();
      unsubscribeCommand();
      releaseTvSse();
    };
  }, [masjidId, user, navigate, masjidData?.subscription]);

  useEffect(() => {
    if (!masjidId || !user) return;
    const currentView = viewMode === "none" ? "multiday" : viewMode;
    const suppressNetwork = Date.now() - remoteViewSyncRef.current < REMOTE_VIEW_SUPPRESS_MS;
    updateTvLastView(currentView, { suppressNetwork });
  }, [viewMode, masjidId, user]);

  // Push settings changes to backend so app remote can sync
  // This runs when any setting changes on the TV side
  useEffect(() => {
    if (!masjidId || !user) return;
    
    // Debounce: skip during initial mount/hydration
    const timer = setTimeout(() => {
      const suppressNetwork = Date.now() - remoteSettingsSyncRef.current < REMOTE_SETTINGS_SUPPRESS_MS;
      updateTvSettings({
        themeMode: tvSettings.themeMode,
        primaryColor: tvSettings.primaryColor,
        showTomorrow: tvSettings.showTomorrow,
        zoomLevel: tvSettings.zoomLevel,
        adhanEnabled: tvSettings.adhanEnabled,
        iqamaEnabled: tvSettings.iqamaEnabled,
        adhanCountdownMinutes: tvSettings.adhanCountdownMinutes,
        iqamaCountdownMinutes: tvSettings.iqamaCountdownMinutes,
        adhanScreenDurationMinutes: tvSettings.adhanScreenDurationMinutes,
        iqamaScreenDurationMinutes: tvSettings.iqamaScreenDurationMinutes,
        jummahSilenceEnabled: tvSettings.jummahSilenceEnabled,
        jummahSilenceDurationMinutes: tvSettings.jummahSilenceDurationMinutes,
        forbiddenScreenEnabled: tvSettings.forbiddenScreenEnabled,
        forbiddenScreenDurationMinutes: tvSettings.forbiddenScreenDurationMinutes,
        tomorrowChangesBanner: tvSettings.tomorrowChangesBanner,
      }, { suppressNetwork });
    }, 100);
    
    return () => clearTimeout(timer);
  }, [
    masjidId,
    user,
    tvSettings.themeMode,
    tvSettings.primaryColor,
    tvSettings.showTomorrow,
    tvSettings.zoomLevel,
    tvSettings.adhanEnabled,
    tvSettings.iqamaEnabled,
    tvSettings.adhanCountdownMinutes,
    tvSettings.iqamaCountdownMinutes,
    tvSettings.adhanScreenDurationMinutes,
    tvSettings.iqamaScreenDurationMinutes,
    tvSettings.jummahSilenceEnabled,
    tvSettings.jummahSilenceDurationMinutes,
    tvSettings.forbiddenScreenEnabled,
    tvSettings.forbiddenScreenDurationMinutes,
    tvSettings.tomorrowChangesBanner,
  ]);

  // Load images with IndexedDB caching - dramatically reduces backend reads
  // Only downloads new/modified images, uses cached blobs for unchanged images
  const imageObjectUrlsRef = useRef([]);
  const revokeImageObjectUrls = () => {
    imageObjectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    imageObjectUrlsRef.current = [];
  };

  useEffect(() => {
    if (!masjidId) return;

    // Track if component is still mounted to avoid state updates after unmount
    let isMounted = true;

    const loadImagesWithCache = async () => {
      try {
        // Step 1: Get lightweight metadata from server (no signed URLs, minimal read cost)
        const metaResp = await getTeleviewImagesMetadata(masjidId);
        if (!metaResp?.ok || !Array.isArray(metaResp.images)) {
          console.log("No images metadata from server");
          return;
        }

        const serverMetadata = metaResp.images;
        
        // Step 2: Compare with local IndexedDB cache to determine what needs syncing
        const { toDownload, toDelete, cached } = await syncWithServer(masjidId, serverMetadata);

        // Step 3: Delete removed images from cache
        for (const img of toDelete) {
          await deleteCachedImage(img.storagePath);
        }

        // Step 4: Download only new/modified images
        let downloadedImages = [];
        if (toDownload.length > 0) {
          // Get full image list with download URLs (only when we need to download)
          const fullResp = await getTeleviewImages(masjidId);
          if (fullResp?.ok && Array.isArray(fullResp.images)) {
            // Create a map for quick lookup
            const urlMap = new Map(fullResp.images.map(img => [img.storagePath, img.url]));
            
            // Download each new/modified image
            downloadedImages = await Promise.all(
              toDownload.map(async (meta) => {
                const downloadUrl = urlMap.get(meta.storagePath);
                if (!downloadUrl) return null;
                
                try {
                  const user = auth.currentUser;
                  const token = user ? await user.getIdToken(false) : null;
                  const res = await fetch(downloadUrl, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                  });
                  if (!res.ok) throw new Error(`Image fetch failed: ${res.status}`);
                  let blob = await res.blob();
                  
                  // Convert HEIC images to JPEG for browser compatibility
                  blob = await convertHeicIfNeeded(blob, meta.name);
                  
                  // Save to IndexedDB cache
                  await saveImage({
                    storagePath: meta.storagePath,
                    masjidId,
                    md5Hash: meta.md5Hash,
                    blob,
                    name: meta.name,
                    uploadedAt: meta.uploadedAt,
                  });
                  
                  return {
                    storagePath: meta.storagePath,
                    blob,
                    name: meta.name,
                    uploadedAt: meta.uploadedAt,
                  };
                } catch (e) {
                  console.error("Error downloading image:", meta.storagePath, e);
                  return null;
                }
              })
            );
            downloadedImages = downloadedImages.filter(Boolean);
          }
        }

        if (!isMounted) return;

        // Step 5: Create object URLs and update state
        // Combine cached images with newly downloaded images
        const allImages = [...cached, ...downloadedImages];
        
        // Sort by uploadedAt to maintain consistent order
        allImages.sort((a, b) => {
          const ta = a.uploadedAt ? Date.parse(a.uploadedAt) : 0;
          const tb = b.uploadedAt ? Date.parse(b.uploadedAt) : 0;
          return ta - tb;
        });
        
        // Create object URLs (converting HEIC if needed)
        const newObjectUrls = [];
        const mapped = await Promise.all(allImages.map(async (img) => {
          // Convert HEIC images from cache if they weren't converted before
          const convertedBlob = await convertHeicIfNeeded(img.blob, img.name);
          const objectUrl = URL.createObjectURL(convertedBlob);
          newObjectUrls.push(objectUrl);
          return {
            id: img.storagePath,
            storagePath: img.storagePath,
            name: img.name,
            uploadedAt: img.uploadedAt ? new Date(img.uploadedAt) : null,
            url: objectUrl,
          };
        }));
        
        // Revoke old URLs and update refs
        revokeImageObjectUrls();
        imageObjectUrlsRef.current = newObjectUrls;
        setImages(mapped);
        
      } catch (error) {
        console.error("Error loading images with cache:", error);
        // On error, try to show cached images if available
        try {
          const cachedImages = await getCachedImagesForMasjid(masjidId);
          if (cachedImages.length > 0 && isMounted) {
            const newObjectUrls = [];
            const mapped = await Promise.all(cachedImages.map(async (img) => {
              // Convert HEIC images from cache if they weren't converted before
              const convertedBlob = await convertHeicIfNeeded(img.blob, img.name);
              const objectUrl = URL.createObjectURL(convertedBlob);
              newObjectUrls.push(objectUrl);
              return {
                id: img.storagePath,
                storagePath: img.storagePath,
                name: img.name,
                uploadedAt: img.uploadedAt ? new Date(img.uploadedAt) : null,
                url: objectUrl,
              };
            }));
            revokeImageObjectUrls();
            imageObjectUrlsRef.current = newObjectUrls;
            setImages(mapped);
          }
        } catch (cacheError) {
          console.error("Error loading from cache:", cacheError);
        }
      }
    };

    loadImagesWithCache();

    // Refresh images every 5 minutes to detect new uploads/deletions
    // Now this is cheap because we only download metadata + changed images
    const interval = setInterval(loadImagesWithCache, 5 * 60 * 1000);
    
    return () => {
      isMounted = false;
      clearInterval(interval);
      revokeImageObjectUrls();
    };
  }, [masjidId, imageRefreshNonce]);

  const loadPrayerTimes = async (mId) => {
    try {
      const tvData = await getTvData(mId);
      if (tvData.ok) {
        setTodayPrayerTimes(tvData.todayPrayerTimes);
        setTomorrowPrayerTimes(tvData.tomorrowPrayerTimes);
      }
    } catch (err) {
      console.error("Error loading prayer times:", err);
    }
  };

  const loadEvents = async (masjidId) => {
    try {
      const result = await getEvents(masjidId);
      if (result.ok && Array.isArray(result.events)) {
        const now = new Date();
        const futureEvents = result.events.filter((event) => {
          const eventDate = event.date ? new Date(event.date) : null;
          return eventDate && eventDate >= now;
        });
        setEvents(futureEvents);
      }
    } catch (err) {
      console.error("Error loading events:", err);
    }
  };

  // Auto-rotate events
  useEffect(() => {
    if (viewMode !== "events" || events.length <= 5) {
      setCurrentEventIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setCurrentEventIndex((prev) =>
        prev + 5 > events.length - 5 ? 0 : prev + 5
      );
    }, 7500);
    return () => clearInterval(interval);
  }, [events.length, viewMode]);

  // Auto-rotate images
  useEffect(() => {
    if (viewMode !== "images" || images.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => prev + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, [images, viewMode]);

  // Reset slider position when images list changes
  useEffect(() => {
    setCurrentImageIndex(0);
    setSliderNoTransition(false);
  }, [images]);

  // After instant-reset to position 0, re-enable transition on the next paint
  useEffect(() => {
    if (!sliderNoTransition) return;
    let raf1, raf2;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setSliderNoTransition(false));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [sliderNoTransition]);

  const visibleEvents = events.slice(currentEventIndex, currentEventIndex + 5);

  // Load images from Firebase Storage for this masjid
  const handleManageImages = () => {
    console.log(
      "Opening image management dialog, current state:",
      showImageDialog
    );
    setShowImageDialog(true);
    console.log("State set to true");
  };

  // Debug: Log dialog state changes
  useEffect(() => {
    console.log("showImageDialog state changed to:", showImageDialog);
  }, [showImageDialog]);

  // Sync cursor style for fullscreen mode when controls hide
  useEffect(() => {
    // When controls are hidden, hide cursor globally (useful in fullscreen)
    if (!controlsVisible) {
      document.documentElement.style.cursor = "none";
    } else {
      document.documentElement.style.cursor = "";
    }

    return () => {
      // restore on unmount
      document.documentElement.style.cursor = "";
    };
  }, [controlsVisible]);

  // Close custom menu when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (!menuOpen) return;
      // If click is inside the bottom-left controls, ignore
      const controls = document.querySelector("[data-tv-controls]");
      if (controls && controls.contains(e.target)) return;
      setMenuOpen(false);
    };

    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [menuOpen]);

  const formatPrayerTime = (prayerData, type = "iqama") => {
    if (!prayerData) return "-";
    if (type === "adhan") return prayerData.adhan || "-";
    return prayerData.iqama || prayerData.adhan || "-";
  };

  const getAdhan = (prayerData) => (prayerData ? prayerData.adhan || "-" : "-");
  const getIqama = (prayerData) => (prayerData ? prayerData.iqama || "-" : "-");

  // Prayer assets - use lucide-react icons like PrayerTimesView
  const PRAYER_ASSETS = {
    Fajr: { icon: "moonstar" },
    Sunrise: { icon: "sunrise" },
    Dhuhr: { icon: "sun" },
    Asr: { icon: "cloudsun" },
    Maghrib: { icon: "sunset" },
    Isha: { icon: "moon" },
  };

  const ICON_NAME_TO_COMPONENT = {
    moonstar: MoonStar,
    sunrise: SunriseIcon,
    sun: Sun,
    cloudsun: CloudSun,
    sunset: Sunset,
    moon: Moon,
  };

  const [currentPrayerName, setCurrentPrayerName] = useState(null);
  const [nextPrayerName, setNextPrayerName] = useState(null);
  const [currentPrayerTomorrow, setCurrentPrayerTomorrow] = useState(null);
  const [showTomorrow, setShowTomorrow] = useState(true);
  const leftRef = useRef(null);
  const headerRef = useRef(null);
  const qrRef = useRef(null);
  const sampleCardRef = useRef(null);

  // Parse a time string like "5:30 AM" into a Date object on today's date
  const parseTimeStringToToday = (timeStr, baseDate = new Date(), prayerName = null) => {
    if (!timeStr || typeof timeStr !== "string") return null;
    const cleaned = timeStr.trim();
    const base = new Date(baseDate);
    
    // First, try matching AM/PM format (if already present, use it)
    const ampmMatch = cleaned.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (ampmMatch) {
      let hh = parseInt(ampmMatch[1], 10);
      const mm = parseInt(ampmMatch[2], 10);
      const ampm = ampmMatch[3].toUpperCase();
      if (ampm === "PM" && hh !== 12) hh += 12;
      if (ampm === "AM" && hh === 12) hh = 0;
      const d = new Date(base);
      d.setHours(hh, mm, 0, 0);
      return d;
    }
    
    // Try time without AM/PM (HH:MM or H:MM)
    const timeMatch = cleaned.match(/^(\d{1,2}):(\d{2})$/);
    if (timeMatch) {
      let hh = parseInt(timeMatch[1], 10);
      const mm = parseInt(timeMatch[2], 10);
      
      // Apply prayer-specific AM/PM logic
      if (prayerName) {
        const prayer = prayerName.toLowerCase();
        if (prayer === "fajr" || prayer === "sunrise") {
          // Fajr and Sunrise are always AM
          if (hh === 12) hh = 0; // 12:xx AM = 00:xx
        } else if (prayer === "dhuhr" || prayer === "duhr" || prayer.startsWith("jummah")) {
          // Duhr and Jummah follow same AM/PM logic
          // AM if between 10:00-11:59, otherwise PM
          if (hh >= 10 && hh <= 11) {
            // AM (10:xx or 11:xx stays as is)
          } else if (hh === 12) {
            // 12:xx PM stays as 12
          } else if (hh >= 1 && hh <= 9) {
            // 1:xx-9:xx means PM for duhr/jummah
            hh += 12;
          }
        } else if (prayer === "asr" || prayer === "maghrib" || prayer === "isha") {
          // Asr, Maghrib, Isha are always PM
          if (hh >= 1 && hh <= 11) hh += 12; // Convert to 24-hour PM
        }
      }
      
      if (hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59) {
        const d = new Date(base);
        d.setHours(hh, mm, 0, 0);
        return d;
      }
    }
    
    // Try to create a date using base date and the time string
    const dateString = `${base.getFullYear()}-${String(
      base.getMonth() + 1
    ).padStart(2, "0")}-${String(base.getDate()).padStart(2, "0")} ${cleaned}`;
    const parsed = new Date(dateString);
    if (!isNaN(parsed.getTime())) return parsed;

    return null;
  };

  // Format a time string for display in cards, ensuring AM/PM when parsable
  const formatCardTime = (timeStr, prayerName = null) => {
    if (!timeStr || timeStr === "-") return "-";
    
    // If already contains AM/PM, normalize spacing and casing
    if (/\bAM\b|\bPM\b/i.test(timeStr)) {
      const d = parseTimeStringToToday(timeStr, currentDate, prayerName);
      if (d && !isNaN(d.getTime())) {
        return d.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      }
      return timeStr;
    }

    // Parse without AM/PM using prayer-specific logic
    const d = parseTimeStringToToday(timeStr, currentDate, prayerName);
    if (d && !isNaN(d.getTime())) {
      return d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }

    // Fallback: return original
    return timeStr;
  };

  // Strips the PM/AM part from the time string for display
  const formatTimeNoSuffix = (timeStr, prayerName = null) => {
    const formatted = formatCardTime(timeStr, prayerName);
    if (!formatted || formatted === "-") return "-";
    return formatted.replace(/\s*(AM|PM)/i, "");
  };

  // Gets just the suffix (AM/PM)
  const getTimeSuffix = (timeStr, prayerName = null) => {
    const formatted = formatCardTime(timeStr, prayerName);
    if (!formatted || formatted === "-") return "";
    const match = formatted.match(/(AM|PM)/i);
    return match ? match[1] : "";
  };

  // Compute current and next prayer names from today's adhan times
  useEffect(() => {
    if (!todayPrayerTimes) {
      setCurrentPrayerName(null);
      setNextPrayerName(null);
      return;
    }

    const now = new Date();

    // Parse prayer times with fallbacks
    const fajrTime = todayPrayerTimes?.fajr?.adhan || todayPrayerTimes?.Fajr?.adhan || "05:00";
    const sunriseTime = todayPrayerTimes?.sunrise || todayPrayerTimes?.Sunrise || "06:30";
    const dhuhrTime = todayPrayerTimes?.duhr?.adhan || todayPrayerTimes?.dhuhr?.adhan || todayPrayerTimes?.Dhuhr?.adhan || "12:00";
    const asrTime = todayPrayerTimes?.asr?.adhan || todayPrayerTimes?.Asr?.adhan || "15:30";
    const maghribTime = todayPrayerTimes?.maghrib?.adhan || todayPrayerTimes?.Maghrib?.adhan || "18:30";
    const ishaTime = todayPrayerTimes?.isha?.adhan || todayPrayerTimes?.Isha?.adhan || "20:00";

    const fajr = parseTimeStringToToday(fajrTime, currentDate, "Fajr");
    const sunrise = parseTimeStringToToday(sunriseTime, currentDate, "Sunrise");
    const extEnd = new Date(sunrise.getTime() + 1.5 * 60 * 60 * 1000); // 1.5 hours after sunrise
    const dhuhr = parseTimeStringToToday(dhuhrTime, currentDate, "Dhuhr");
    const asr = parseTimeStringToToday(asrTime, currentDate, "Asr");
    const maghrib = parseTimeStringToToday(maghribTime, currentDate, "Maghrib");
    const isha = parseTimeStringToToday(ishaTime, currentDate, "Isha");

    let currentPrayerName, nextPrayerName;

    if (now >= fajr && now < sunrise) {
      currentPrayerName = "Fajr";
      nextPrayerName = "Sunrise";
    } else if (now >= sunrise && now < dhuhr) {
      currentPrayerName = "Sunrise";
      nextPrayerName = "Dhuhr";
    } else if (now >= dhuhr && now < asr) {
      currentPrayerName = "Dhuhr";
      nextPrayerName = "Asr";
    } else if (now >= asr && now < maghrib) {
      currentPrayerName = "Asr";
      nextPrayerName = "Maghrib";
    } else if (now >= maghrib && now < isha) {
      currentPrayerName = "Maghrib";
      nextPrayerName = "Isha";
    } else if (now >= isha) {
      // After Isha - Isha is current, Fajr is next (tomorrow's Fajr)
      currentPrayerName = "Isha";
      nextPrayerName = "Fajr";
      // After Isha means next Fajr is tomorrow
      setCurrentPrayerTomorrow(true);
    } else {
      // Before Fajr (midnight to Fajr) - no current, Fajr is next (TODAY's Fajr)
      currentPrayerName = null;
      nextPrayerName = "Fajr";
      // Before today's Fajr means next Fajr is today, not tomorrow
      setCurrentPrayerTomorrow(false);
    }

    setCurrentPrayerName(currentPrayerName);
    setNextPrayerName(nextPrayerName);
  }, [todayPrayerTimes, currentDate]);

  // Simplified responsive logic - compute directly, no effects fighting each other
  const [spaceTooSmall, setSpaceTooSmall] = useState(false);
  const [showJummahAndQR, setShowJummahAndQR] = useState(true);
  const [showAyah, setShowAyah] = useState(false);
  
  // showTomorrowCards is simply controlled by settings + whether data exists
  const showTomorrowCards = !!(todayPrayerTimes && tomorrowPrayerTimes && tvSettings.showTomorrow);

  // Only handle space-dependent UI (spaceTooSmall, showJummahAndQR, showAyah)
  useEffect(() => {
    const evaluate = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // For horizontal: if right panel is shown, account for it
      const rightPanelWidth = viewMode !== "none" ? viewportHeight : 0;
      const availableLeftWidth = viewportWidth - rightPanelWidth;

      // Constants for minimum sizes
      const minHeaderHeight = viewportHeight * 0.15;
      const minCardHeight = viewportHeight * 0.12;
      const minAyahHeight = viewportHeight * 0.2;
      const minJummahQRHeight = viewportHeight * 0.14;
      const minGaps = viewportHeight * 0.12;
      const minCardWidth = viewportHeight * 0.2;
      const needsTwoColumns = availableLeftWidth >= minCardWidth * 2.2;
      const todayHeight = minHeaderHeight + 3 * minCardHeight + minGaps;

      // Check if viewport is too small
      if (viewportHeight < todayHeight || !needsTwoColumns) {
        setSpaceTooSmall(true);
        setShowJummahAndQR(false);
        setShowAyah(false);
        return;
      }

      setSpaceTooSmall(false);
      
      let remainingHeight = viewportHeight - todayHeight;
      setShowJummahAndQR(remainingHeight >= minJummahQRHeight);
      if (remainingHeight >= minJummahQRHeight) {
        remainingHeight -= minJummahQRHeight;
      }

      const is1080pOrHigher = viewportWidth >= 1920 && viewportHeight >= 1000;
      setShowAyah(viewMode !== "none" && is1080pOrHigher && remainingHeight >= minAyahHeight);
    };

    evaluate();
    window.addEventListener("resize", evaluate);
    return () => window.removeEventListener("resize", evaluate);
  }, [viewMode]); // Only depends on viewMode now

  // Define isDark early so it can be used in loading/error states
  const isDark = localAppearance === "dark";

  const formatDate = (date) =>
    date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  const formatTime = (date) =>
    date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

  // Collect up to three Jummah times from today's prayer times
  const jummahTimes = [];
  if (todayPrayerTimes?.jummah) {
    ["1", "2", "3"].forEach((k) => {
      const t = todayPrayerTimes.jummah[k];
      if (t) jummahTimes.push(t);
    });
  }
  
  // Collect khutbah times
  const khutbahTimes = [];
  if (todayPrayerTimes?.khutbah) {
    ["1", "2", "3"].forEach((k) => {
      const t = todayPrayerTimes.khutbah[k];
      if (t) khutbahTimes.push(t);
    });
  }

  // AyahCard: measurement-based single-pass fit (no iterative jitter) - FIXED SIZE 20vh
  function AyahCard({
    ayahIndexProp,
    controlsVisibleProp,
    setAyahIndexProp,
    primaryColor,
    isDark,
  }) {
    const ayah =
      Array.isArray(ayatData) && ayatData[ayahIndexProp]
        ? ayatData[ayahIndexProp]
        : null;
    const totalAyat = Array.isArray(ayatData) ? ayatData.length : 0;
    const referenceLabel = ayah ? `[${ayah.reference}]` : "";
    const containerRef = useRef(null);
    const contentRef = useRef(null);
    const lastAyahRef = useRef(null);
    
    // Base font sizes (in rem)
    const BASE_ARABIC = 1.8;
    const BASE_ENGLISH = 0.95;
    const MIN_SCALE = 0.25;
    
    const [fontScale, setFontScale] = useState(1);

    const goPrev = () => {
      if (totalAyat) setAyahIndexProp((p) => (p - 1 + totalAyat) % totalAyat);
    };
    const goNext = () => {
      if (totalAyat) setAyahIndexProp((p) => (p + 1) % totalAyat);
    };

    // Iterative font scaling: binary search for the right scale
    useLayoutEffect(() => {
      if (!containerRef.current || !contentRef.current || !ayah) return;
      
      // Reset to check with fresh ayah
      const ayahKey = `${ayahIndexProp}-${ayah.arabic}`;
      if (lastAyahRef.current === ayahKey) return;
      lastAyahRef.current = ayahKey;
      
      const containerHeight = containerRef.current.clientHeight;
      const arabicEl = contentRef.current.querySelector('[data-arabic]');
      const englishEl = contentRef.current.querySelector('[data-english]');
      
      if (!arabicEl || !englishEl) return;
      
      // Binary search for optimal scale
      let low = MIN_SCALE;
      let high = 1;
      
      for (let i = 0; i < 12; i++) {
        const testScale = (low + high) / 2;
        
        // Apply test scale
        arabicEl.style.fontSize = `${BASE_ARABIC * testScale}rem`;
        englishEl.style.fontSize = `${BASE_ENGLISH * testScale}rem`;
        
        // Force reflow
        void contentRef.current.offsetHeight;
        
        const contentHeight = contentRef.current.scrollHeight;
        
        // Leave more room for navigation and padding (25% margin)
        if (contentHeight <= containerHeight * 0.75) {
          low = testScale;
        } else {
          high = testScale;
        }
      }
      
      // Use the lower bound (guaranteed to fit)
      setFontScale(low);
    }, [ayah, ayahIndexProp]);

    if (!ayah) return null;

    const currentColor = primaryColor || "#1976d2";
    const arabicSize = BASE_ARABIC * fontScale;
    const englishSize = BASE_ENGLISH * fontScale;

    return (
      <Card
        className="shadow-xl overflow-hidden relative"
        style={{
          borderRadius: "2.5vh",
          backgroundColor: isDark ? "rgba(23, 23, 23, 0.6)" : "rgba(255, 253, 249, 0.7)",
          border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid #e8e4dc",
          width: "100%",
          height: "100%",
          minHeight: 0,
          maxHeight: "100%",
          flexShrink: 0,
        }}
      >
        <CardContent
          ref={containerRef}
          className="w-full h-full flex flex-col justify-center box-border overflow-hidden relative"
          style={{ padding: "1.5vh" }}
        >
          <div ref={contentRef} className="w-full" style={{ overflow: "hidden" }}>
            {/* Arabic Text - Right aligned */}
            <div
              data-arabic
              dir="rtl"
              style={{
                fontSize: `${arabicSize}rem`,
                lineHeight: 1.5,
                textAlign: "right",
                fontFamily: "NotoNaskh, serif",
                color: isDark ? "#ffffff" : "#0f172a",
                paddingBottom: "1vh",
                width: "100%",
                wordBreak: "keep-all",
                overflowWrap: "anywhere",
                whiteSpace: "normal",
              }}
            >
              {ayah.arabic || "[Arabic text missing]"}
            </div>

            {/* English Translation - Left aligned */}
            <p
              data-english
              style={{
                fontSize: `${englishSize}rem`,
                lineHeight: 1.4,
                textAlign: "left",
                color: isDark ? "#9ca3af" : "#4b5563",
                width: "100%",
                margin: 0,
                wordBreak: "normal",
                overflowWrap: "break-word",
              }}
            >
              {ayah.english || "[English translation missing]"}
            </p>
          </div>

          {/* Navigation arrows */}
          <div
            className="absolute flex items-center transition-opacity duration-300"
            style={{
              bottom: "0.8vh",
              left: "50%",
              transform: "translateX(-50%)",
              gap: "0.6vh",
              opacity: controlsVisibleProp ? 1 : 0,
              pointerEvents: controlsVisibleProp ? "auto" : "none",
            }}
          >
            <Button
              variant="ghost"
              size="icon"
              onClick={goPrev}
              className="p-0 min-w-0"
              style={{
                width: "2vh",
                height: "2vh",
                color: currentColor,
              }}
            >
              <ChevronLeft style={{ fontSize: "1.1vh" }} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={goNext}
              className="p-0 min-w-0"
              style={{
                width: "2vh",
                height: "2vh",
                color: currentColor,
              }}
            >
              <ChevronRight style={{ fontSize: "1.1vh" }} />
            </Button>
          </div>

          {/* Reference label */}
          {referenceLabel && (
            <p
              className="absolute font-semibold italic"
              style={{
                bottom: "1.6vh",
                right: "2vh",
                fontSize: "0.75rem",
                color: currentColor,
              }}
            >
              {referenceLabel}
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  if (authLoading || loading) {
    return (
      <div
        className={`flex justify-center items-center h-screen w-screen ${
          isDark ? "bg-[#1a1a1a]" : "bg-gray-100"
        }`}
      >
        <Loader2 className="h-16 w-16 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`flex flex-col justify-center items-center h-screen w-screen gap-8 ${
          isDark ? "bg-[#1a1a1a]" : "bg-gray-100"
        }`}
      >
        <h2 className="text-2xl font-bold text-destructive">{error}</h2>
      </div>
    );
  }

  // Responsive sizing based on viewport height (vh units for consistency)
  const qrSize = "12vh"; // Scales with viewport height
  const qrSizePixels = Math.round(window.innerHeight * 0.12); // Convert vh to pixels for QR code
  const jummahCardHeight = "calc(12vh + 2.5vh)"; // QR height + text below
  const controlButtonSize = "3vh"; // Button size scales proportionally
  const iconSize = "2vh"; // Icon size scales proportionally

  return (
    <div
      className={`fixed top-0 left-0 w-screen h-screen flex overflow-hidden z-10 m-0 p-0 font-sans ${
        isDark ? "text-white" : "text-slate-900"
      }`}
      onClick={() => {
        setMenuOpen(false);
        setThemeModeMenuOpen(false);
        setZoomMenuOpen(false);
      }}
      style={{
        backgroundColor: isDark ? "#0a0a0a" : "#faf7f2",
        cursor: controlsVisible ? "auto" : "none",
        position: "relative",
      }}
    >
      {/* View Switching Loading Overlay */}
      {viewSwitching && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center"
          style={{
            backgroundColor: isDark ? "rgba(10,10,10,0.95)" : "rgba(250,247,242,0.95)",
            backdropFilter: "blur(8px)",
          }}
        >
          <div className="flex flex-col items-center gap-4">
            <Loader2
              className="animate-spin"
              style={{
                width: "8vh",
                height: "8vh",
                color: primaryColor,
              }}
            />
            <p
              className="text-lg font-medium"
              style={{
                fontSize: "2.5vh",
                color: isDark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)",
              }}
            >
              Switching View...
            </p>
          </div>
        </div>
      )}

      {/* Adhan/Iqama/Jummah/Forbidden Countdown & Announcement Overlay */}
      {!settingsOpen && <CountdownOverlay
        overlayMode={countdownOverlay.overlayMode}
        overlayPrayerName={countdownOverlay.overlayPrayerName}
        overlayCountdownSeconds={countdownOverlay.overlayCountdownSeconds}
        overlayType={countdownOverlay.overlayType}
        screenDimmed={countdownOverlay.screenDimmed}
        iqamaExtraDimmed={countdownOverlay.iqamaExtraDimmed}
        masjidData={masjidData}
        primaryColor={primaryColor}
        isDark={isDark}
        simplifiedPrayerTimes={todayPrayerTimes ? {
          fajr: { adhan: todayPrayerTimes?.fajr?.adhan || "-", iqama: todayPrayerTimes?.fajr?.iqama || "-" },
          sunrise: { adhan: todayPrayerTimes?.sunrise || "-", iqama: null },
          dhuhr: { adhan: todayPrayerTimes?.duhr?.adhan || "-", iqama: todayPrayerTimes?.duhr?.iqama || "-" },
          asr: { adhan: todayPrayerTimes?.asr?.adhan || "-", iqama: todayPrayerTimes?.asr?.iqama || "-" },
          maghrib: { adhan: todayPrayerTimes?.maghrib?.adhan || "-", iqama: todayPrayerTimes?.maghrib?.iqama || "-" },
          isha: { adhan: todayPrayerTimes?.isha?.adhan || "-", iqama: todayPrayerTimes?.isha?.iqama || "-" },
        } : null}
      />}

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
           style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }}
      />

      {/* Bottom-left Controls - Simplified: Back, Fullscreen, Settings */}
      <TVControls
        visible={controlsVisible}
        isDark={isDark}
        isFullscreen={isFullscreen}
        onBack={() => {
          sessionStorage.setItem('exit-fullscreen', 'true');
          sessionStorage.removeItem('tv-fullscreen-state');
          navigate(isSuperAdmin ? "/admin-dashboard" : "/masjid-dashboard");
        }}
        onFullscreenToggle={handleFullscreenToggle}
        onSettingsClick={() => setSettingsOpen(true)}
        extraControls={
          TEST_MODE && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (testMode === "adhan") {
                    if (testTimerRef.current) clearInterval(testTimerRef.current);
                    setTestMode(null);
                    clearOverlay();
                  } else {
                    if (testTimerRef.current) clearInterval(testTimerRef.current);
                    setTestMode("adhan");
                    setOverlayMode("countdown");
                    setOverlayPrayerName("Fajr");
                    setOverlayType("adhan");
                    setOverlayCountdownSeconds(10);

                    let seconds = 10;
                    let adhanScreenSeconds = 0;
                    testTimerRef.current = setInterval(() => {
                      seconds--;
                      if (seconds > 0) {
                        setOverlayCountdownSeconds(seconds);
                      } else if (seconds === 0) {
                        seconds = -1;
                        setOverlayMode("adhan");
                      } else {
                        adhanScreenSeconds++;
                        // Auto-stop after 10 seconds of adhan screen
                        if (adhanScreenSeconds >= 10) {
                          clearInterval(testTimerRef.current);
                          testTimerRef.current = null;
                          setTestMode(null);
                          clearOverlay();
                        }
                      }
                    }, 1000);
                  }
                }}
                style={{
                  width: "4vh",
                  height: "4vh",
                  borderRadius: "1.2vh",
                  background: testMode === "adhan" ? "rgba(34, 197, 94, 0.5)" : "rgba(34, 197, 94, 0.2)",
                  border: `2px solid ${testMode === "adhan" ? "rgba(34, 197, 94, 0.9)" : "rgba(34, 197, 94, 0.5)"}`,
                  backdropFilter: "blur(10px)",
                  boxShadow: testMode === "adhan" ? "0 0 12px rgba(34, 197, 94, 0.5)" : "none",
                }}
                className="hover:scale-110 transition-transform duration-200"
                title={testMode === "adhan" ? "Stop Adhan Test" : "Test Adhan Sequence"}
              >
                <span style={{ fontSize: "1.5vh", color: "#22c55e", fontWeight: "bold" }}>A</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (testMode === "iqama") {
                    if (testTimerRef.current) clearInterval(testTimerRef.current);
                    setTestMode(null);
                    clearOverlay();
                  } else {
                    if (testTimerRef.current) clearInterval(testTimerRef.current);
                    setTestMode("iqama");
                    setOverlayMode("countdown");
                    setOverlayPrayerName("Fajr");
                    setOverlayType("iqama");
                    setOverlayCountdownSeconds(10);
                    setIqamaExtraDimmed(false);

                    let seconds = 10;
                    let iqamaSeconds = 0;
                    let extraDimApplied = false;
                    testTimerRef.current = setInterval(() => {
                      if (seconds > 0) {
                        seconds--;
                        setOverlayCountdownSeconds(seconds);
                      } else if (seconds === 0) {
                        seconds = -1;
                        setOverlayMode("iqama");
                        setScreenDimmed(true);
                      } else {
                        iqamaSeconds++;
                        // Auto-stop after 10 seconds of iqama screen
                        if (iqamaSeconds >= 10) {
                          clearInterval(testTimerRef.current);
                          testTimerRef.current = null;
                          setTestMode(null);
                          clearOverlay();
                        } else if (iqamaSeconds >= 5 && !extraDimApplied) {
                          extraDimApplied = true;
                          setIqamaExtraDimmed(true);
                        }
                      }
                    }, 1000);
                  }
                }}
                style={{
                  width: "4vh",
                  height: "4vh",
                  borderRadius: "1.2vh",
                  background: testMode === "iqama" ? "rgba(239, 68, 68, 0.5)" : "rgba(239, 68, 68, 0.2)",
                  border: `2px solid ${testMode === "iqama" ? "rgba(239, 68, 68, 0.9)" : "rgba(239, 68, 68, 0.5)"}`,
                  backdropFilter: "blur(10px)",
                  boxShadow: testMode === "iqama" ? "0 0 12px rgba(239, 68, 68, 0.5)" : "none",
                }}
                className="hover:scale-110 transition-transform duration-200"
                title={testMode === "iqama" ? "Stop Iqama Test" : "Test Iqama Sequence"}
              >
                <span style={{ fontSize: "1.5vh", color: "#ef4444", fontWeight: "bold" }}>I</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (testMode === "jummah") {
                    if (testTimerRef.current) clearInterval(testTimerRef.current);
                    setTestMode(null);
                    clearOverlay();
                  } else {
                    if (testTimerRef.current) clearInterval(testTimerRef.current);
                    setTestMode("jummah");
                    setOverlayMode("jummah");
                    setOverlayPrayerName("Jummah");
                    setOverlayType("jummah");
                    setScreenDimmed(true);
                    setIqamaExtraDimmed(false);

                    let jummahSeconds = 0;
                    let extraDimApplied = false;
                    testTimerRef.current = setInterval(() => {
                      jummahSeconds++;
                      // Auto-stop after 10 seconds
                      if (jummahSeconds >= 10) {
                        clearInterval(testTimerRef.current);
                        testTimerRef.current = null;
                        setTestMode(null);
                        clearOverlay();
                      } else if (jummahSeconds >= 5 && !extraDimApplied) {
                        extraDimApplied = true;
                        setIqamaExtraDimmed(true);
                      }
                    }, 1000);
                  }
                }}
                style={{
                  width: "4vh",
                  height: "4vh",
                  borderRadius: "1.2vh",
                  background: testMode === "jummah" ? "rgba(168, 85, 247, 0.5)" : "rgba(168, 85, 247, 0.2)",
                  border: `2px solid ${testMode === "jummah" ? "rgba(168, 85, 247, 0.9)" : "rgba(168, 85, 247, 0.5)"}`,
                  backdropFilter: "blur(10px)",
                  boxShadow: testMode === "jummah" ? "0 0 12px rgba(168, 85, 247, 0.5)" : "none",
                }}
                className="hover:scale-110 transition-transform duration-200"
                title={testMode === "jummah" ? "Stop Jummah Test" : "Test Jummah Silence"}
              >
                <span style={{ fontSize: "1.5vh", color: "#a855f7", fontWeight: "bold" }}>J</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (testMode === "sunrise") {
                    if (testTimerRef.current) clearInterval(testTimerRef.current);
                    setTestMode(null);
                    clearOverlay();
                  } else {
                    if (testTimerRef.current) clearInterval(testTimerRef.current);
                    setTestMode("sunrise");
                    setOverlayMode("forbidden");
                    setOverlayPrayerName("Sunrise");
                    setOverlayType("forbidden");
                    setOverlayCountdownSeconds(10);
                    setScreenDimmed(false);

                    let seconds = 10;
                    testTimerRef.current = setInterval(() => {
                      seconds--;
                      if (seconds > 0) {
                        setOverlayCountdownSeconds(seconds);
                      } else {
                        clearInterval(testTimerRef.current);
                        testTimerRef.current = null;
                        setTestMode(null);
                        clearOverlay();
                      }
                    }, 1000);
                  }
                }}
                style={{
                  width: "4vh",
                  height: "4vh",
                  borderRadius: "1.2vh",
                  background: testMode === "sunrise" ? "rgba(251, 191, 36, 0.5)" : "rgba(251, 191, 36, 0.2)",
                  border: `2px solid ${testMode === "sunrise" ? "rgba(251, 191, 36, 0.9)" : "rgba(251, 191, 36, 0.5)"}`,
                  backdropFilter: "blur(10px)",
                  boxShadow: testMode === "sunrise" ? "0 0 12px rgba(251, 191, 36, 0.5)" : "none",
                }}
                className="hover:scale-110 transition-transform duration-200"
                title={testMode === "sunrise" ? "Stop Sunrise Test" : "Test Forbidden Prayer Time"}
              >
                <span style={{ fontSize: "1.5vh", color: "#fbbf24", fontWeight: "bold" }}>S</span>
              </Button>
            </>
          )
        }
      />

      {/* Settings Dialog */}
      <TVSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={tvSettings}
        isTeleView={true}
        onViewChange={(newView) => {
          if (newView === VIEW_MODES.TODAY) {
            navigateToPrayerTimesView();
          } else if (newView === VIEW_MODES.MULTIDAY) {
            setViewMode("none");
          } else if (newView === VIEW_MODES.EVENTS) {
            setViewMode("events");
          } else if (newView === VIEW_MODES.IMAGES) {
            setViewMode("images");
          }
        }}
        onManageImages={() => setShowImageDialog(true)}
        isImageView={viewMode === "images"}
      />

      {/* Image Management Dialog */}
      <ImageUploadDialog
        masjidId={masjidId}
        open={showImageDialog}
        onOpenChange={setShowImageDialog}
        onImagesChanged={() => setImageRefreshNonce((prev) => prev + 1)}
        primaryColor={tvSettings.primaryColor}
      />

      {/* Left Side - Prayer Times */}
      <div
        ref={leftRef}
        className="h-screen flex flex-col box-border"
        style={{
          flex: viewMode === "none" ? "1 1 auto" : "0 0 auto",
          width: viewMode === "none" ? "auto" : `calc(100vw - ${squareSize})`,
          minWidth: viewMode === "none" ? 0 : `calc(100vw - ${squareSize})`,
          maxHeight: "100vh",
          backgroundColor: isDark ? "#0a0a0a" : "#faf7f2",
          padding: "1vh 0.5vh", // Reduced horizontal padding to prevent wrapping
          borderRight:
            viewMode !== "none"
              ? `0.2vh solid ${
                  isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
                }`
              : "none",
          gap: "0.8vh",
          overflow: "hidden",
        }}
      >
        {/* Error message if space is too small */}
        {spaceTooSmall && (
          <div
            className="absolute top-1/2 left-1/2 text-center z-[15] p-8 rounded-lg shadow-lg"
            style={{
              transform: "translate(-50%, -50%)",
              backgroundColor: isDark
                ? "rgba(0,0,0,0.9)"
                : "rgba(255,255,255,0.9)",
            }}
          >
            <h2 className="text-4xl font-bold mb-4 text-destructive">
              Screen Too Small
            </h2>
            <p className="text-base text-muted-foreground">
              Please increase the window size or use a larger display.
              <br />
              This view works best on 16:9 displays at 720p or higher.
            </p>
          </div>
        )}
        {/* Date & Time Header - Enhanced */}
        <div ref={headerRef} className="flex-shrink-0" style={{ margin: "-1vh -0.5vh 0 -0.5vh", width: "calc(100% + 1vh)" }}>
          <div
            className="flex items-start justify-between backdrop-blur-sm"
            style={{
              gap: "2vh",
              padding: "1.5vh 2vh",
              backgroundColor: isDark ? 'rgba(10, 10, 10, 0.5)' : 'rgba(250, 247, 242, 0.5)',
              borderBottom: `2px solid ${
                isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
              }`,
            }}
          >
            <div className="min-w-0 flex-1">
              <h1
                className="font-bold"
                style={{
                  color: isDark ? "#ffffff" : "#0f172a",
                  fontSize: "clamp(1rem, 3.5vh, 3.5rem)",
                  lineHeight: 1,
                  letterSpacing: "-0.02em",
                  whiteSpace: "nowrap",
                }}
              >
                {formatDate(currentDate)}
              </h1>
              <h2
                style={{
                  fontSize: "clamp(1.2rem, 2.5vh, 2.5rem)",
                  fontWeight: 600,
                  color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
                  marginTop: "0.5vh",
                }}
              >
                {masjidData?.name || "Masjid"}
              </h2>
            </div>

            <div className="flex-shrink-0 flex flex-col items-end" style={{ marginLeft: "1.5vh" }}>
              <h1
                className="font-bold text-right"
                style={{
                  fontSize: "clamp(2.5rem, 5vh, 5rem)",
                  lineHeight: 1,
                  color: isDark ? "#ffffff" : "#0f172a",
                  fontVariantNumeric: "tabular-nums",
                  letterSpacing: "-0.02em",
                }}
              >
                {formatTime(currentDate)}
              </h1>
              <h2
                style={{
                  fontSize: "clamp(1.2rem, 2.5vh, 2.5rem)",
                  fontWeight: 700,
                  marginTop: "0.5vh",
                  color: primaryColor,
                }}
              >
                Muslim+
              </h2>
            </div>
          </div>
        </div>

        {/* Prayer times grid - Today and optionally Tomorrow */}
        <div
          className="flex"
          style={{
            gap: viewMode === "none" ? "2vh" : "1.5vh",
            flex: "1 1 auto",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <div
            className="flex flex-col"
            style={{
              flex: "1 1 0",
              minWidth: 0,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            {showTomorrowCards && (
              <h2
                className="font-bold flex-shrink-0"
                style={{
                  marginBottom: "0.6vh",
                  fontSize: "clamp(1rem, 1.8vh, 1.6rem)",
                }}
              >
                Today
              </h2>
            )}
            <div
              className="grid w-full"
              style={{
                gridTemplateColumns: "repeat(2, 1fr)",
                gridTemplateRows: "repeat(3, 1fr)",
                gap: viewMode === "none" ? "1vh" : "0.8vh",
                flex: "1 1 auto",
                minHeight: 0,
                overflow: "hidden",
                height: "100%",
              }}
            >
              {[
                ["Fajr", todayPrayerTimes?.fajr],
                ["Sunrise", { adhan: todayPrayerTimes?.sunrise }],
                ["Dhuhr", todayPrayerTimes?.duhr],
                ["Asr", todayPrayerTimes?.asr],
                ["Maghrib", todayPrayerTimes?.maghrib],
                ["Isha", todayPrayerTimes?.isha],
              ].map(([name, data]) => {
                const isCurrent = currentPrayerName === name;
                // Highlight next prayer in today's cards:
                // - For non-Fajr prayers: always highlight if it's next
                // - For Fajr: only highlight if we're before today's Fajr (currentPrayerTomorrow is false)
                const isNext = nextPrayerName === name && (nextPrayerName !== "Fajr" || !currentPrayerTomorrow);
                const iconName = PRAYER_ASSETS[name]?.icon;
                const IconComp = ICON_NAME_TO_COMPONENT[iconName] || Clock;
                const adhan = getAdhan(data);
                const iqama = getIqama(data);

                return (
                  <div
                    key={name}
                    style={{ minHeight: 0, minWidth: 0, overflow: "hidden" }}
                  >
                    <PrayerCard
                      name={name}
                      adhan={adhan}
                      iqama={iqama}
                      isCurrent={isCurrent}
                      isNext={isNext}
                      IconComp={IconComp}
                      isDark={isDark}
                      viewMode={viewMode}
                      primaryColor={primaryColor}
                      formatCardTime={formatCardTime}
                      formatTimeNoSuffix={formatTimeNoSuffix}
                      getTimeSuffix={getTimeSuffix}
                      showTomorrowCards={showTomorrowCards}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {showTomorrowCards && tomorrowPrayerTimes && (
            <div
              className="flex flex-col"
              style={{
                flex: "1 1 0",
                minWidth: 0,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <h2
                className="font-bold flex-shrink-0"
                style={{
                  marginBottom: "0.6vh",
                  fontSize: "clamp(1rem, 1.8vh, 1.6rem)",
                }}
              >
                Tomorrow
              </h2>
              <div
                className="grid w-full"
                style={{
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gridTemplateRows: "repeat(3, 1fr)",
                  gap: viewMode === "none" ? "1vh" : "0.8vh",
                  flex: "1 1 auto",
                  minHeight: 0,
                  overflow: "hidden",
                  height: "100%",
                }}
              >
                {[
                  ["Fajr", tomorrowPrayerTimes?.fajr],
                  ["Sunrise", { adhan: tomorrowPrayerTimes?.sunrise }],
                  ["Dhuhr", tomorrowPrayerTimes?.duhr],
                  ["Asr", tomorrowPrayerTimes?.asr],
                  ["Maghrib", tomorrowPrayerTimes?.maghrib],
                  ["Isha", tomorrowPrayerTimes?.isha],
                ].map(([name, data]) => {
                  const isCurrent = false;
                  // Highlight Fajr in tomorrow only if it's the next prayer AND we're after today's Isha (not before today's Fajr)
                  const isNext = nextPrayerName === "Fajr" && name === "Fajr" && currentPrayerTomorrow;
                  const iconName = PRAYER_ASSETS[name]?.icon;
                  const IconComp = ICON_NAME_TO_COMPONENT[iconName] || Clock;
                  const adhan = getAdhan(data);
                  const iqama = getIqama(data);

                  return (
                    <div
                      key={name}
                      style={{ minHeight: 0, minWidth: 0, overflow: "hidden" }}
                    >
                      <PrayerCard
                        name={name}
                        adhan={adhan}
                        iqama={iqama}
                        isCurrent={isCurrent}
                        isNext={isNext}
                        IconComp={IconComp}
                        isDark={isDark}
                        viewMode={viewMode}
                        primaryColor={primaryColor}
                        formatCardTime={formatCardTime}
                        formatTimeNoSuffix={formatTimeNoSuffix}
                        getTimeSuffix={getTimeSuffix}
                        showTomorrowCards={showTomorrowCards}
                        isTomorrow={true}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Render Ayah under grids - show in none view too, in events/images view it's between prayer times and jummah */}
        {showAyah && (
          <div
            className="flex-shrink-0"
            style={{
              marginTop: "1vh",
              height: viewMode === "none" ? "16vh" : "14vh",
              minHeight: viewMode === "none" ? "16vh" : "14vh",
              maxHeight: viewMode === "none" ? "16vh" : "14vh",
              overflow: "hidden",
            }}
          >
            <AyahCard
              ayahIndexProp={ayahIndex}
              controlsVisibleProp={controlsVisible}
              setAyahIndexProp={setAyahIndex}
              primaryColor={primaryColor}
              isDark={isDark}
            />
          </div>
        )}

        {/* QR Code / Jummah bottom area - only show if we have space */}
        {showJummahAndQR &&
          (viewMode === "none" ? (
            <div
              className="relative w-full flex-shrink-0"
              style={{
                marginTop: "auto",
                paddingTop: "0.8vh",
                minHeight: 0,
              }}
            >
              {/* Three-column layout: Muslim+ QR | Jummah cards (fill space) | Website QR */}
              <div
                className="flex items-start justify-between"
                style={{ gap: "1.2vh", overflow: "hidden", minHeight: 0 }}
              >
                {/* Muslim+ QR code on far left */}
                <div
                  className="flex flex-col items-center justify-start flex-shrink-0"
                  style={{ minWidth: qrSize, marginRight: "1vh" }}
                >
                  <div
                    className="bg-white flex items-center justify-center"
                    style={{
                      width: qrSize,
                      height: qrSize,
                      borderRadius: "1.5vh",
                      padding: "1vh",
                    }}
                  >
                    <QRCodeWrapper
                      value="https://muslimplus.org"
                      size={qrSizePixels}
                    />
                  </div>
                  <p
                    className="whitespace-nowrap font-semibold"
                    style={{
                      marginTop: "0.5vh",
                      fontSize: "1.2rem",
                      color: primaryColor,
                    }}
                  >
                    Muslim+
                  </p>
                </div>

                {/* Jummah cards - fill space between QR codes */}
                <div
                  className="flex items-center overflow-hidden"
                  style={{
                    gap: "1vh",
                    flex: "1 1 auto",
                    paddingRight: "1vh",
                  }}
                >
                  {jummahTimes.length === 0 ? (
                    <Card
                      className="shadow-sm flex items-center justify-center"
                      style={{
                        height: jummahCardHeight,
                        flex: "1 1 0",
                        borderRadius: "0.5vh",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: isDark ? "transparent" : "#e8e4dc",
                        backgroundColor: isDark ? "#171717" : "#fffdf9",
                      }}
                    >
                      <CardContent
                        className="flex flex-col items-center justify-center"
                        style={{ padding: "1vh" }}
                      >
                        <p
                          className="text-muted-foreground whitespace-nowrap"
                          style={{ fontSize: "1.2rem" }}
                        >
                          No Jummah times
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    jummahTimes.slice(0, 3).map((t, i) => (
                      <PrayerCard
                        key={i}
                        name={jummahTimes.length === 1 ? "Jummah" : `Jummah ${["I", "II", "III"][i]}`}
                        adhan={khutbahTimes[i] || null}
                        iqama={t}
                        isJummah={true}
                        cardSx={{
                          flex: "1 1 0",
                          height: jummahCardHeight,
                          minWidth: 0,
                        }}
                        isDark={isDark}
                        viewMode={viewMode}
                        primaryColor={primaryColor}
                        formatCardTime={formatCardTime}
                        formatTimeNoSuffix={formatTimeNoSuffix}
                        getTimeSuffix={getTimeSuffix}
                      />
                    ))
                  )}
                </div>

                {/* QR code on far right */}
                {masjidData?.website && (
                  <div
                    ref={qrRef}
                    className="flex flex-col items-center justify-start flex-shrink-0"
                  >
                    <div
                      className="bg-white flex items-center justify-center"
                      style={{
                        width: qrSize,
                        height: qrSize,
                        borderRadius: "1.5vh",
                        padding: "1vh",
                      }}
                    >
                      <QRCodeWrapper
                        value={masjidData.website}
                        size={qrSizePixels}
                      />
                    </div>
                    <p
                      className="whitespace-nowrap"
                      style={{
                        marginTop: "0.5vh",
                        fontSize: "1.2rem",
                      }}
                    >
                      {masjidData?.nickname || "Website"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div
              className="flex-shrink-0 w-full"
              style={{
                marginTop: "auto",
                paddingTop: "0.8vh",
                overflow: "hidden",
                minHeight: 0,
              }}
            >
              <div
                className="flex items-start justify-between"
                style={{ gap: "1.2vh", overflow: "hidden", minHeight: 0 }}
              >
                {/* Muslim+ QR code on far left */}
                <div
                  className="flex flex-col items-center justify-start flex-shrink-0"
                  style={{ minWidth: qrSize, marginRight: "1vh" }}
                >
                  <div
                    className="bg-white flex items-center justify-center"
                    style={{
                      width: qrSize,
                      height: qrSize,
                      borderRadius: "1.5vh",
                      padding: "1vh",
                    }}
                  >
                    <QRCodeWrapper
                      value="https://muslimplus.org"
                      size={qrSizePixels}
                    />
                  </div>
                  <p
                    className="whitespace-nowrap font-semibold"
                    style={{
                      marginTop: "0.5vh",
                      fontSize: "1.2rem",
                      color: primaryColor,
                    }}
                  >
                    Muslim+
                  </p>
                </div>

                <div
                  className="flex items-center overflow-hidden"
                  style={{
                    gap: "1vh",
                    flex: "1 1 auto",
                    paddingRight: "1vh",
                  }}
                >
                  {jummahTimes.length === 0 ? (
                    <Card
                      className="shadow-sm flex items-center justify-center"
                      style={{
                        height: jummahCardHeight,
                        minWidth: "15vh",
                        borderRadius: "0.5vh",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: isDark ? "transparent" : "#e8e4dc",
                        backgroundColor: isDark ? "#171717" : "#fffdf9",
                      }}
                    >
                      <CardContent
                        className="flex flex-col items-center justify-center"
                        style={{ padding: "1.5vh" }}
                      >
                        <p
                          className="text-muted-foreground"
                          style={{ fontSize: "1.2rem" }}
                        >
                          No Jummah times
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    jummahTimes.slice(0, 3).map((t, i) => (
                      <PrayerCard
                        key={i}
                        name={jummahTimes.length === 1 ? "Jummah" : `Jummah ${["I", "II", "III"][i]}`}
                        adhan={khutbahTimes[i] || null}
                        iqama={t}
                        isJummah={true}
                        cardSx={{
                          flex: "1 1 0",
                          height: jummahCardHeight,
                          minWidth: 0,
                        }}
                        isDark={isDark}
                        viewMode={viewMode}
                        primaryColor={primaryColor}
                        formatCardTime={formatCardTime}
                        formatTimeNoSuffix={formatTimeNoSuffix}
                        getTimeSuffix={getTimeSuffix}
                      />
                    ))
                  )}
                </div>

                {masjidData?.website && (
                  <div
                    ref={qrRef}
                    className="flex flex-col items-center justify-start"
                  >
                    <div
                      className="bg-white flex items-center justify-center"
                      style={{
                        width: qrSize,
                        height: qrSize,
                        borderRadius: "1.5vh",
                        padding: "1vh",
                      }}
                    >
                      <QRCodeWrapper
                        value={masjidData.website}
                        size={qrSizePixels}
                      />
                    </div>
                    <p
                      className="whitespace-nowrap"
                      style={{
                        marginTop: "0.5vh",
                        fontSize: "1.2rem",
                      }}
                    >
                      {masjidData?.nickname || "Website"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        {/* Hidden sample card for sizing calculations */}
        <div
          className="absolute invisible pointer-events-none"
          style={{
            height: 0,
            width: 0,
          }}
        >
          <Card
            ref={sampleCardRef}
            style={{
              width: leftRef.current
                ? leftRef.current.clientWidth / 2 - 12
                : 180,
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Clock />
                <p>Sample</p>
              </div>
              <h2 className="text-4xl">12:00</h2>
              <p style={{ color: "#D4AF37" }}>13:00</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right Side - Events / Images (fixed square) - animated */}
      <div
        aria-hidden={viewMode === "none"}
        className="flex justify-center items-center box-border transition-all duration-700 relative"
        style={{
          position: viewMode === "none" ? "absolute" : "relative",
          right: viewMode === "none" ? "-100%" : 0,
          width: viewMode === "none" ? 0 : squareSize,
          flex: viewMode === "none" ? "0 0 0" : `0 0 ${squareSize}`,
          transitionTimingFunction: "cubic-bezier(.2,.8,.2,1)",
          opacity: viewMode === "none" ? 0 : 1,
          height: "100vh",
          pointerEvents: viewMode === "none" ? "none" : "auto",
          overflow: "hidden",
        }}
      >
        {!spaceTooSmall && (
          <div
            className="flex flex-col box-border overflow-hidden transition-all duration-300"
            style={{
              width: "100%",
              height: "100%",
              padding: viewMode === "images" ? 0 : sizeMultiplier >= 1 ? "4vh" : "2vh",
              backgroundColor:
                viewMode === "images" && isDark ? "#000" : "transparent",
              transitionTimingFunction: "cubic-bezier(.2,.8,.2,1)",
              transform: "none",
              transformOrigin: "center center",
            }}
          >
            {viewMode === "events" ? (
              <div className="relative h-full flex flex-col">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1.5vh",
                    paddingBottom: "2.5vh",
                    borderBottom: `2px solid ${
                      isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
                    }`,
                    marginBottom: "2vh",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "4.5vh",
                      height: "4.5vh",
                      borderRadius: "1.2vh",
                      background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)`,
                      boxShadow: `0 4px 12px ${primaryColor}40`,
                    }}
                  >
                    <Calendar style={{ fontSize: "2.5vh", color: "white" }} />
                  </div>
                  <h2
                    className="font-bold"
                    style={{
                      color: isDark ? "#fff" : "#1a1a1a",
                      fontSize: "2.8rem",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Upcoming Events
                  </h2>
                </div>

                {events.length === 0 ? (
                  <div className="flex flex-col justify-center items-center flex-1 w-full">
                    <div
                      style={{
                        width: "12vh",
                        height: "12vh",
                        borderRadius: "50%",
                        background: isDark
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.05)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "3vh",
                      }}
                    >
                      <Calendar
                        style={{
                          fontSize: "6vh",
                          color: isDark
                            ? "rgba(255,255,255,0.3)"
                            : "rgba(0,0,0,0.3)",
                        }}
                      />
                    </div>
                    <p
                      style={{
                        fontSize: "2rem",
                        color: isDark
                          ? "rgba(255,255,255,0.5)"
                          : "rgba(0,0,0,0.5)",
                        fontWeight: 500,
                      }}
                    >
                      No upcoming events
                    </p>
                  </div>
                ) : (
                  <div className="w-full h-full overflow-hidden relative" style={{ flex: "1 1 0", minHeight: 0 }}>
                    <div
                      className="flex h-full transition-transform duration-700 ease-out"
                      style={{
                        transform: `translateX(-${Math.floor(currentEventIndex / 5) * 100}%)`,
                        willChange: "transform",
                      }}
                    >
                      {Array.from({
                        length: Math.max(1, Math.ceil(events.length / 5)),
                      }).map((_, pageIndex) => (
                        <div
                          key={pageIndex}
                          className="flex flex-col w-full"
                          style={{
                            flex: "0 0 100%",
                            gap: "1.2vh",
                            minHeight: 0,
                            overflow: "hidden",
                          }}
                        >
                          {events.slice(pageIndex * 5, (pageIndex + 1) * 5).map((event) => (
                            <EventCard
                              key={event.recurrenceInstanceId || event.id}
                              event={event}
                              isDark={isDark}
                              primaryColor={primaryColor}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Event page indicator dots (one per page of 5 events) */}
                <div
                  className="absolute flex transition-opacity duration-300"
                  style={{
                    bottom: `${1.2 / sizeMultiplier}vh`,
                    left: "50%",
                    transform: `translateX(-50%) scale(${1 / sizeMultiplier})`,
                    transformOrigin: "center bottom",
                    gap: "1vh",
                    opacity: controlsVisible ? 1 : 0,
                    pointerEvents: controlsVisible ? "auto" : "none",
                  }}
                >
                  {Array.from({
                    length: Math.max(1, Math.ceil(events.length / 5)),
                  }).map((_, page) => {
                    const pageIndex = Math.floor(currentEventIndex / 5);
                    return (
                      <div
                        key={page}
                        onClick={() => setCurrentEventIndex(page * 5)}
                        className="rounded-full cursor-pointer"
                        style={{
                          width: "1vh",
                          height: "1vh",
                          backgroundColor:
                            page === pageIndex
                              ? primaryColor
                              : isDark
                              ? "rgba(255,255,255,0.45)"
                              : "rgba(0,0,0,0.25)",
                          boxShadow: "0 0 0 0.2vh rgba(0,0,0,0.08)",
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            ) : // Image Slideshow - Full panel
            images.length === 0 ? (
              <div className="flex justify-center items-center w-full h-full">
                <p
                  className="text-muted-foreground"
                  style={{ fontSize: "2rem" }}
                >
                  No images uploaded
                </p>
              </div>
            ) : (
              // Sliding carousel: container with track and slides
              <div className="w-full h-full overflow-hidden relative">
                <div
                  className="tv-slider-track flex h-full"
                  style={{
                    transform: `translateX(-${currentImageIndex * 100}%)`,
                    transition: sliderNoTransition
                      ? "none"
                      : "transform 500ms cubic-bezier(0.4, 0, 0.2, 1)",
                    willChange: "transform",
                  }}
                  onTransitionEnd={() => {
                    if (currentImageIndex >= images.length) {
                      setSliderNoTransition(true);
                      setCurrentImageIndex(0);
                    }
                  }}
                >
                  {[...images, images[0]].map((img, idx) => (
                    <div
                      key={idx === images.length ? `clone-${img?.id}` : img.id}
                      className="flex items-center justify-center overflow-hidden"
                      style={{
                        flex: "0 0 100%",
                        height: "100%",
                        backgroundColor: isDark ? "#1a1a1a" : "#f5f5f5",
                      }}
                    >
                      <img
                        src={img?.url}
                        alt={img?.name || `Slide ${idx + 1}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </div>
                  ))}
                </div>
                {/* Navigation dots (visible only when controls are visible) */}
                <div
                  className="absolute flex transition-opacity duration-300"
                  style={{
                    bottom: `${1.2 / sizeMultiplier}vh`,
                    left: "50%",
                    transform: `translateX(-50%) scale(${1 / sizeMultiplier})`,
                    transformOrigin: "center bottom",
                    gap: "1vh",
                    opacity: controlsVisible ? 1 : 0,
                    pointerEvents: controlsVisible ? "auto" : "none",
                  }}
                >
                  {images.map((_, idx) => (
                    <div
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className="rounded-full cursor-pointer"
                      style={{
                        width: "1vh",
                        height: "1vh",
                        backgroundColor:
                          idx === currentImageIndex % images.length
                            ? primaryColor
                            : isDark
                            ? "rgba(255,255,255,0.45)"
                            : "rgba(0,0,0,0.25)",
                        boxShadow: "0 0 0 0.2vh rgba(0,0,0,0.08)",
                      }}
                    />
                  ))}
                </div>
                {/* Ayah card removed from images overlay per request */}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Wake lock to prevent screen sleep */}
      <WakeLockVideo />
    </div>
  );
}

// Event Card Component - Redesigned for better visibility and modern look
function EventCard({ event, isDark, primaryColor }) {
  const eventDate = event.date?.toDate
    ? event.date.toDate()
    : new Date(event.date);
  const formattedDate = eventDate.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const dayNumber = eventDate.getDate();
  const monthName = eventDate.toLocaleDateString("en-US", { month: "short" });
  
  const eventTimeStr =
    event.time && typeof event.time === "string" && event.time.trim() !== ""
      ? event.time
      : eventDate && !isNaN(eventDate.getTime())
      ? eventDate.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
      : null;

  return (
    <div
      className="flex w-full box-border transition-all duration-500 hover:scale-[1.02]"
      style={{
        flex: "1 1 0",
        minHeight: "14vh",
        maxHeight: "20vh",
        overflow: "hidden",
        borderRadius: "2vh",
        background: isDark 
          ? "linear-gradient(145deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.03) 100%)" 
          : "linear-gradient(145deg, #ffffff 0%, #fcfcfc 100%)",
        border: isDark 
          ? "1px solid rgba(255,255,255,0.1)" 
          : "1px solid rgba(0,0,0,0.05)",
        boxShadow: isDark
          ? "0 8px 32px rgba(0,0,0,0.2)"
          : "0 8px 24px rgba(0,0,0,0.06)",
        backdropFilter: "blur(10px)",
      }}
    >
      {/* Date Box */}
      <div
        className="flex flex-col items-center justify-center flex-shrink-0"
        style={{
          width: "12vh",
          background: isDark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.02)",
          borderRight: isDark
            ? "1px solid rgba(255,255,255,0.05)"
            : "1px solid rgba(0,0,0,0.05)",
        }}
      >
        <span
          style={{
            fontSize: "2.4vh", // Increased from 2.2vh
            fontWeight: 600,
            color: primaryColor,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
          }}
        >
          {monthName}
        </span>
        <span
          style={{
            fontSize: "5vh", // Increased from 4.5vh
            fontWeight: 700,
            lineHeight: 1,
            color: isDark ? "#fff" : "#1a1a1a",
            marginTop: "0.5vh",
          }}
        >
          {dayNumber}
        </span>
      </div>

      {/* Content */}
      <div
        className="flex flex-col justify-center flex-1 overflow-hidden"
        style={{
          padding: "1.5vh 2.5vh",
          gap: "0.8vh",
        }}
      >
        {/* Title */}
        <h3
          className="font-bold"
          style={{
            fontSize: "clamp(1.4rem, 3vh, 3.5rem)", // Increased from 2.6vh/2.2rem
            lineHeight: 1.2,
            color: isDark ? "#fff" : "#0f172a",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {event.title}
        </h3>

        {/* Time & Location Row */}
        <div className="flex items-center flex-wrap" style={{ gap: "2vh", marginTop: "0.5vh" }}>
          {eventTimeStr && (
            <div
              className="flex items-center"
              style={{
                background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
                padding: "0.6vh 1.2vh",
                borderRadius: "0.8vh",
                gap: "0.8vh",
              }}
            >
              <Clock
                style={{
                  width: "1.8vh",
                  height: "1.8vh",
                  color: primaryColor,
                }}
              />
              <span
                style={{
                  fontSize: "1.8vh",
                  fontWeight: 600,
                  color: isDark ? "#e2e8f0" : "#475569",
                }}
              >
                {eventTimeStr}
              </span>
            </div>
          )}

          {event.location && (
            <div
              className="flex items-center"
              style={{ gap: "0.8vh" }}
            >
              <MapPin
                style={{
                  width: "1.8vh",
                  height: "1.8vh",
                  color: primaryColor,
                }}
              />
              <span
                style={{
                  fontSize: "1.8vh",
                  color: isDark ? "rgba(255,255,255,0.7)" : "#64748b",
                  fontWeight: 500,
                  maxWidth: "25vh",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {event.location}
              </span>
            </div>
          )}
        </div>

        {/* Description (if present and space allows) */}
        {event.description && (
          <p
            style={{
              fontSize: "clamp(0.9rem, 1.6vh, 1.1rem)",
              lineHeight: 1.5,
              color: isDark ? "rgba(255,255,255,0.5)" : "#94a3b8",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              marginTop: "0.5vh",
            }}
          >
            {event.description}
          </p>
        )}
      </div>
    </div>
  );
}
